<template>
  <div class="w-full space-y-4 text-slate-100">

    <!-- TOP BAR -->
    <div class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg pageShell">
      <div class="px-4 py-4 sm:px-5 sm:py-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">

          <!-- LEFT: breadcrumb + title -->
          <div class="min-w-0">
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
              <ol role="list" class="flex items-center gap-2 text-xs text-slate-400">
                <li class="flex items-center">
                  <router-link
                    to="/hub"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-200 transition hover:bg-slate-600"
                  >
                    <HomeIcon class="h-4 w-4" aria-hidden="true" />
                    <span class="sr-only">Home</span>
                  </router-link>
                </li>
                <li class="flex items-center gap-2">
                  <ChevronRightIcon class="h-4 w-4 text-slate-500" />
                  <router-link to="/dashboard/routes" class="font-medium text-slate-300 transition hover:text-white">
                    Trasy
                  </router-link>
                </li>
                <li class="flex items-center gap-2">
                  <ChevronRightIcon class="h-4 w-4 text-slate-500" />
                  <span class="font-medium text-slate-300">Podgląd trasy</span>
                </li>
              </ol>
            </nav>

            <div class="flex items-center sm:hidden">
              <router-link
                to="/dashboard/routes"
                class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-700 px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-600"
              >
                <ArrowLeftIcon class="h-4 w-4" />
                Cofnij
              </router-link>
            </div>

            <div class="mt-3" v-if="route">
              <div class="flex flex-wrap items-center gap-2">
                <div class="text-lg font-semibold tracking-tight text-white truncate">
                  {{ route.name && route.name.length > 0 ? route.name : 'Podgląd trasy' }}
                </div>
                <span class="statusChip statusChipBlue">
                  <MapPinIcon class="h-3.5 w-3.5" />
                  {{ route.mainRegion?.name || '—' }}
                </span>
                <span
                  class="statusChip"
                  :class="routeStatusChipClass(route.status)"
                >
                  {{ routeStatusLabel(route.status) }}
                </span>
                <span v-if="editMode" class="statusChip statusChipYellow">
                  <PencilSquareIcon class="h-3.5 w-3.5" />
                  Tryb edycji
                </span>
              </div>
              <div class="mt-1 text-xs text-slate-400">
                Szczegóły trasy, dane kierowcy, zestawu transportowego i przypisanych zamówień.
              </div>
            </div>
          </div>

          <!-- RIGHT: action buttons -->
          <div v-if="route" class="flex flex-wrap items-center gap-2">
            <button
              v-if="!editMode"
              type="button"
              @click="enableEditMode()"
              class="btnSecondary"
            >
              <PencilSquareIcon class="h-4 w-4" />
              Edytuj trasę
            </button>

            <template v-if="editMode">
              <button
                type="button"
                @click="cancelEditMode()"
                class="btnSecondary"
              >
                Anuluj
              </button>
              <button
                type="button"
                @click="saveRouteEdits()"
                :disabled="saving"
                class="btnPrimary"
              >
                <span v-if="saving">Zapisywanie...</span>
                <span v-else>Zapisz zmiany</span>
              </button>
            </template>

            <button
              v-if="!editMode"
              type="button"
              :disabled="sent.bulkLoading"
              @click="generateSentForRoute()"
              :class="['btnSecondary', sent.bulkLoading ? 'opacity-50 cursor-not-allowed' : '']"
            >
              <ArrowTopRightOnSquareIcon class="h-4 w-4" />
              <span v-if="sent.bulkLoading">Wysyłanie...</span>
              <span v-else>Generuj SENT</span>
            </button>

            <button
              v-if="!editMode"
              type="button"
              @click="downloadWaybillsPdfForAllOrders()"
              class="btnSecondary"
            >
              <ArrowDownTrayIcon class="h-4 w-4" />
              Listy przewozowe
            </button>

            <button
              v-if="!editMode && hasInvoiceableOrders"
              type="button"
              @click="generateAllInvoices()"
              :disabled="generatingAllInvoices"
              class="btnPrimary"
            >
              <span v-if="generatingAllInvoices">Generowanie...</span>
              <span v-else>Generuj faktury</span>
            </button>

          </div>

        </div>
      </div>
    </div>

    <!-- MAIN CONTENT -->
    <div v-if="route !== null" class="space-y-4">

      <!-- INFO CARDS ROW -->
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">

        <!-- ROUTE INFO -->
        <section class="sectionCard">
          <div class="sectionHead">
            <div class="flex items-center justify-between gap-3">
              <div>
                <div class="sectionTitle">Informacje o trasie</div>
                <div class="sectionSub">Daty, region i dodatkowe notatki.</div>
              </div>
              <span class="chipBadge">
                <FlagIcon class="h-3.5 w-3.5" />
                Trasa
              </span>
            </div>
          </div>

          <div class="sectionBody space-y-3">
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 min-w-0">

              <!-- LEFT: podstawowe dane -->
              <div class="space-y-3 min-w-0">
                <div class="infoGroupLabel">Podstawowe dane</div>

                <div class="softBox" v-if="!editMode">
                  <div class="softBoxLabel">Nazwa trasy</div>
                  <div class="softBoxValue text-blue-300">
                    {{ route.name && route.name.length > 0 ? route.name : 'Brak' }}
                  </div>
                </div>
                <div class="fieldWrap" v-else>
                  <label class="fieldLabel">Nazwa trasy</label>
                  <input v-model="editData.name" type="text" class="fieldInput" placeholder="Nazwa trasy" />
                </div>

                <div class="softBox" v-if="!editMode">
                  <div class="softBoxLabel">Status trasy</div>
                  <div class="softBoxValue" :class="routeStatusColor(route.status)">
                    {{ routeStatusLabel(route.status) }}
                  </div>
                </div>
                <div class="fieldWrap" v-else>
                  <label class="fieldLabel">Status trasy</label>
                  <select v-model="editData.status" class="fieldInput">
                    <option :value="1">Zaplanowana</option>
                    <option :value="2">W realizacji</option>
                    <option :value="3" :disabled="!canFinishRoute">Zakończona{{ !canFinishRoute ? ' (niedostępne)' : '' }}</option>
                    <option :value="4" :disabled="hasDeliveredOrders">Anulowana{{ hasDeliveredOrders ? ' (trasa ma dostarczone zamówienia)' : '' }}</option>
                  </select>
                  <p v-if="editData.status === 3 && !canFinishRoute" class="mt-1.5 text-xs text-yellow-400">
                    {{ finishBlockReason }}
                  </p>
                  <p v-if="editData.status === 4 && hasDeliveredOrders" class="mt-1.5 text-xs text-yellow-400">
                    Nie można anulować trasy z dostarczonymi zamówieniami.
                  </p>
                </div>

                <template v-if="!editMode">
                  <div class="softBox">
                    <div class="softBoxLabel">Data wyjazdu</div>
                    <div class="softBoxValue">
                      {{ unixToMomentLL(route.plannedStart.unix || route.plannedStart.date.seconds || route.plannedStart.date._seconds) }}
                    </div>
                  </div>
                  <div class="softBox">
                    <div class="softBoxLabel">Data powrotu</div>
                    <div class="softBoxValue">
                      {{ unixToMomentLL(route.plannedEnd.unix || route.plannedEnd.date.seconds || route.plannedEnd.date._seconds) }}
                    </div>
                  </div>
                </template>
                <div class="fieldWrap" v-else>
                  <label class="fieldLabel">Data wyjazdu – powrotu</label>
                  <div class="datePickerWrap" v-if="!addOrdersModal.show && !unloadModal.show && !undeliveredConfirm.show">
                    <VueDatePicker
                      placeholder="Wybierz datę i godzinę wyjazdu..."
                      partial-flow
                      :flow="['calendar', 'time']"
                      :is-24="true"
                      locale="pl"
                      v-model="editDate"
                      range
                      :format="periodDateFormat"
                      cancelText="Anuluj"
                      selectText="Wybierz"
                    />
                  </div>
                  <div v-else class="datePickerFake backdrop-soft">
                    <CalendarDaysIcon class="h-4 w-4 text-slate-500 shrink-0" />
                    <span class="datePickerFakeText">{{ editDate ? periodDateFormat() : 'Wybierz datę i godzinę wyjazdu...' }}</span>
                  </div>
                </div>
              </div>

              <!-- RIGHT: regiony -->
              <div class="space-y-3 min-w-0">
                <div class="infoGroupLabel">Lokalizacje</div>

                <div class="softBox" v-if="!editMode">
                  <div class="softBoxLabel">Region główny</div>
                  <div class="softBoxValue text-blue-300">
                    {{ route.mainRegion?.name?.length > 0 ? route.mainRegion.name : 'Brak' }}
                  </div>
                </div>
                <div class="fieldWrap" v-else>
                  <label class="fieldLabel">Region główny</label>
                  <Listbox as="div" v-model="editSelectedRegion">
                    <div class="relative">
                      <ListboxButton class="listboxButton">
                        <span class="block truncate">{{ editSelectedRegion === null ? 'Wybierz region...' : editSelectedRegion.name }}</span>
                        <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                        </span>
                      </ListboxButton>
                      <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                        <ListboxOptions class="listboxOptions">
                          <ListboxOption as="template" v-for="region in regions.all" :key="region.id" :value="region" v-slot="{ active, selected }">
                            <li :class="[active ? 'bg-indigo-600 text-white' : 'text-slate-100', 'relative cursor-pointer select-none rounded-xl px-3 py-2']">
                              <div class="flex items-center justify-between gap-3">
                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ region.name }}</span>
                                <CheckIcon v-if="selected" class="h-5 w-5 text-white" />
                              </div>
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                </div>

                <template v-if="route.allRegions.filter(r => r !== route.mainRegion.name).length > 0">
                  <div
                    v-for="(region, index) in route.allRegions.filter(r => r !== route.mainRegion.name)"
                    :key="index"
                    class="softBox"
                  >
                    <div class="softBoxLabel">Region dodatkowy {{ index + 1 }}</div>
                    <div class="softBoxValue">{{ region }}</div>
                  </div>
                </template>
                <div v-else class="softBox">
                  <div class="softBoxLabel">Regiony dodatkowe</div>
                  <div class="softBoxValue text-slate-500">Brak dodatkowych regionów</div>
                </div>
              </div>

            </div>

            <!-- Notatka — pełna szerokość -->
            <div class="softBox" v-if="!editMode">
              <div class="softBoxLabel">Notatka</div>
              <div class="softBoxValue" :class="route.note && route.note.length > 0 ? 'text-slate-200' : 'text-slate-500'">
                {{ route.note && route.note.length > 0 ? route.note : 'Brak notatki' }}
              </div>
            </div>
            <div class="fieldWrap" v-else>
              <label class="fieldLabel">Notatka</label>
              <textarea v-model="editData.note" rows="4" class="fieldInput fieldTextarea" placeholder="Notatki do trasy"></textarea>
            </div>

          </div>
        </section>

        <!-- DRIVER + VEHICLE -->
        <div class="space-y-4">

          <!-- DRIVER -->
          <section class="sectionCard">
            <div class="sectionHead">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <div class="sectionTitle">Kierowca</div>
                  <div class="sectionSub">Dane kontaktowe przypisanego kierowcy.</div>
                </div>
                <span class="chipBadge">
                  <UserIcon class="h-3.5 w-3.5" />
                  Kierowca
                </span>
              </div>
            </div>

            <div class="sectionBody space-y-3">
              <template v-if="!editMode">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div class="softBox">
                    <div class="softBoxLabel">Imię i nazwisko</div>
                    <div class="softBoxValue text-blue-300">{{ route.driver?.name || '—' }}</div>
                  </div>
                  <div class="softBox">
                    <div class="softBoxLabel">Telefon</div>
                    <div class="softBoxValue">{{ route.driver?.phone || '—' }}</div>
                  </div>
                </div>
                <div class="softBox">
                  <div class="softBoxLabel">E-mail</div>
                  <div class="softBoxValue">{{ route.driver?.email || '—' }}</div>
                </div>
              </template>
              <div class="fieldWrap" v-else>
                <label class="fieldLabel">Kierowca</label>
                <Listbox as="div" v-model="editSelectedDriver" v-if="drivers.all.length > 0">
                  <div class="relative">
                    <ListboxButton class="listboxButton">
                      <span class="block truncate">{{ editSelectedDriver === null ? 'Wybierz kierowcę...' : (editSelectedDriver.name + ' ' + editSelectedDriver.surname) }}</span>
                      <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                      </span>
                    </ListboxButton>
                    <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                      <ListboxOptions class="listboxOptions">
                        <ListboxOption as="template" v-for="driver in drivers.all" :key="driver.id" :value="driver" v-slot="{ active, selected }">
                          <li :class="[active ? 'bg-indigo-600 text-white' : 'text-slate-100', 'relative cursor-pointer select-none rounded-xl px-3 py-2']">
                            <div class="flex items-center justify-between gap-3">
                              <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ driver.name }} {{ driver.surname }}</span>
                              <CheckIcon v-if="selected" class="h-5 w-5 text-white" />
                            </div>
                          </li>
                        </ListboxOption>
                      </ListboxOptions>
                    </transition>
                  </div>
                </Listbox>
                <div v-else class="text-xs text-slate-400">Ładowanie...</div>
              </div>
            </div>
          </section>

          <!-- VEHICLE + TRAILER -->
          <section class="sectionCard">
            <div class="sectionHead">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <div class="sectionTitle">Zestaw transportowy</div>
                  <div class="sectionSub">Pojazd, naczepa i łączna pojemność.</div>
                </div>
                <span class="chipBadge">
                  <TruckIcon class="h-3.5 w-3.5" />
                  Flota
                </span>
              </div>
            </div>

            <div class="sectionBody space-y-3">
              <template v-if="!editMode">
                <!-- Vehicle -->
                <div v-if="route.vehicle">
                  <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Pojazd</div>
                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div class="softBox">
                      <div class="softBoxLabel">Marka / Model</div>
                      <div class="softBoxValue text-blue-300">
                        {{ route.vehicle.brand }} {{ route.vehicle.model }}
                      </div>
                    </div>
                    <div class="softBox">
                      <div class="softBoxLabel">Rejestracja</div>
                      <div class="softBoxValue font-mono">{{ route.vehicle.licensePlate || '—' }}</div>
                    </div>
                    <div class="softBox">
                      <div class="softBoxLabel">Pojemność</div>
                      <div class="softBoxValue">{{ route.vehicle.tankLitreCapacity != null ? route.vehicle.tankLitreCapacity + ' L' : '—' }}</div>
                    </div>
                  </div>
                  <div v-if="route.vehicle.chambers && route.vehicle.chambers.length > 0" class="mt-2">
                    <div class="mb-1 text-xs font-semibold text-slate-500">Komory pojazdu:</div>
                    <div class="flex flex-wrap gap-2">
                      <span v-for="(ch, ci) in route.vehicle.chambers" :key="'vc'+ci" class="chamberChip">K{{ ci + 1 }}: {{ ch.capacity }} L</span>
                    </div>
                  </div>

                  <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div class="softBox">
                      <div class="softBoxLabel">Nr zezwolenia / zaświadczenia / licencji</div>
                      <div class="softBoxValue">{{ route.vehicle.roadPermissionNumber || '—' }}</div>
                    </div>
                    <div class="softBox">
                      <div class="softBoxLabel">Nr urządzenia / lokalizatora GPS</div>
                      <div class="softBoxValue">{{ route.vehicle.gpsTrackerNumber || '—' }}</div>
                    </div>
                  </div>
                </div>
                <EmptyState v-else />

                <!-- Trailer -->
                <div v-if="route.trailer">
                  <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Naczepa</div>
                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div class="softBox">
                      <div class="softBoxLabel">Marka / Model</div>
                      <div class="softBoxValue text-blue-300">{{ route.trailer.brand }} {{ route.trailer.model }}</div>
                    </div>
                    <div class="softBox">
                      <div class="softBoxLabel">Rejestracja</div>
                      <div class="softBoxValue font-mono">{{ route.trailer.licensePlate || '—' }}</div>
                    </div>
                    <div class="softBox">
                      <div class="softBoxLabel">Pojemność</div>
                      <div class="softBoxValue">{{ route.trailer.tankLitreCapacity != null ? route.trailer.tankLitreCapacity + ' L' : '—' }}</div>
                    </div>
                  </div>
                  <div v-if="route.trailer.chambers && route.trailer.chambers.length > 0" class="mt-2">
                    <div class="mb-1 text-xs font-semibold text-slate-500">Komory naczepy:</div>
                    <div class="flex flex-wrap gap-2">
                      <span v-for="(ch, ci) in route.trailer.chambers" :key="'tc'+ci" class="chamberChip">K{{ ci + 1 }}: {{ ch.capacity }} L</span>
                    </div>
                  </div>
                </div>

                <!-- Total capacity -->
                <div class="softBox softBoxStrong">
                  <div class="softBoxLabel">Łączna pojemność transportowa</div>
                  <div class="softBoxValue softBoxValueStrong text-green-300">{{ route.totalLitreCapacity || 0 }} L</div>
                </div>
              </template>

              <!-- EDIT MODE: vehicle + trailer selectors -->
              <template v-else>
                <div class="fieldWrap">
                  <label class="fieldLabel">Pojazd</label>
                  <Listbox as="div" v-model="editSelectedVehicles" v-if="vehicles.length > 0">
                    <div class="relative">
                      <ListboxButton class="listboxButton">
                        <span class="flex items-center gap-2 truncate">
                          <span class="statusDot bg-green-400" />
                          <span class="block truncate">{{ editSelectedVehicles === null ? 'Wybierz pojazd...' : fullSelectedVehicle }}</span>
                        </span>
                        <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                        </span>
                      </ListboxButton>
                      <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                        <ListboxOptions class="listboxOptions">
                          <ListboxOption as="template" v-for="vehicle in vehicles" :key="vehicle.id" :disabled="!vehicle.available || !vehicle.active" :value="vehicle" v-slot="{ active }">
                            <li v-tooltip="`${vehicle.available ? 'Dostępny' : 'Niedostępny'}`" :class="[!vehicle.available || !vehicle.active ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer', active ? 'bg-indigo-600 text-white' : 'text-slate-100', 'relative select-none rounded-xl px-3 py-2']">
                              <div class="flex items-center justify-between gap-3">
                                <div class="flex min-w-0 items-center gap-3">
                                  <span :class="[vehicle.active ? (vehicle.available ? 'bg-green-400' : 'bg-red-500') : 'bg-yellow-500', 'statusDot']" />
                                  <span class="block truncate">{{ vehicle.brand }} <span class="font-semibold">{{ vehicle.licensePlate }}</span></span>
                                </div>
                                <span class="shrink-0 text-xs font-semibold">{{ vehicle.tankLitreCapacity }} L</span>
                              </div>
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                  <div v-else class="text-xs text-slate-400">Ładowanie...</div>
                </div>

                <div class="fieldWrap">
                  <label class="fieldLabel">Naczepa</label>
                  <Listbox as="div" v-model="editSelectedTrails" v-if="trails.length > 0">
                    <div class="relative">
                      <ListboxButton class="listboxButton">
                        <span class="flex items-center gap-2 truncate">
                          <span class="statusDot bg-green-400" />
                          <span class="block truncate">{{ editSelectedTrails === null ? 'Brak naczepy' : fullSelectedTrail }}</span>
                        </span>
                        <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                        </span>
                      </ListboxButton>
                      <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                        <ListboxOptions class="listboxOptions">
                          <ListboxOption v-if="editSelectedTrails !== null" as="template" :value="null" v-slot="{ active }">
                            <li :class="[active ? 'bg-indigo-600 text-white' : 'text-red-300', 'relative cursor-pointer select-none rounded-xl px-3 py-2']">
                              <span class="font-semibold">Usuń naczepę</span>
                            </li>
                          </ListboxOption>
                          <ListboxOption as="template" v-for="trail in trails" :key="trail.id" :disabled="!trail.available || !trail.active" :value="trail" v-slot="{ active }">
                            <li v-tooltip="`${trail.available ? 'Dostępny' : 'Niedostępny'}`" :class="[!trail.available || !trail.active ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer', active ? 'bg-indigo-600 text-white' : 'text-slate-100', 'relative select-none rounded-xl px-3 py-2']">
                              <div class="flex items-center justify-between gap-3">
                                <div class="flex min-w-0 items-center gap-3">
                                  <span :class="[trail.active ? (trail.available ? 'bg-green-400' : 'bg-red-500') : 'bg-yellow-500', 'statusDot']" />
                                  <span class="block truncate">{{ trail.brand }} <span class="font-semibold">{{ trail.licensePlate }}</span></span>
                                </div>
                                <span class="shrink-0 text-xs font-semibold">{{ trail.tankLitreCapacity }} L</span>
                              </div>
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                  <div v-else class="text-xs text-slate-400">Ładowanie...</div>
                </div>
              </template>
            </div>
          </section>

        </div>
      </div>

      <!-- ORDERS TABLE -->
      <div class="overflow-hidden border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg pageShell">

        <!-- Table header -->
        <div class="border-b border-slate-700 px-4 py-4 sm:px-5">
          <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div class="min-w-0">
              <div class="text-sm font-semibold text-white">Zamówienia w trasie</div>
              <div class="mt-1 text-xs text-slate-400">
                {{ editMode ? editData.orders.length : (route.orders?.length || 0) }} zamówień przypisanych do trasy
                <span v-if="editMode" class="text-yellow-300 ml-2">— przeciągnij wiersze, aby zmienić kolejność</span>
              </div>
            </div>
            <div class="flex items-center gap-2 self-start lg:self-auto">
              <button
                v-if="editMode"
                type="button"
                @click="openAddOrdersModal()"
                class="btnSecondary"
              >
                <PlusIcon class="h-4 w-4" />
                Dodaj punkty
              </button>
              <span class="chipBadge">
                <ClipboardDocumentListIcon class="h-3.5 w-3.5" />
                Zamówienia
              </span>
            </div>
          </div>
        </div>

        <div class="w-full overflow-x-auto">
          <table class="min-w-[900px] w-full divide-y divide-slate-700">
            <thead class="bg-slate-700 bg-opacity-70">
              <tr>
                <th v-if="editMode" class="py-3.5 pl-3 pr-1 text-center text-xs font-semibold uppercase tracking-wide text-slate-300" style="width: 50px;">Kol.</th>
                <th class="py-3.5 pl-5 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">Miasto dostawy</th>
                <th class="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">Adres dostawy</th>
                <th class="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">Klient</th>
                <th class="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">Produkt</th>
                <th class="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">Ilość</th>
                <th class="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">Status</th>
                <th class="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">SENT</th>
                <th class="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">Faktura</th>
                <th class="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">Pliki</th>
              </tr>
            </thead>

            <tbody class="divide-y divide-slate-700 bg-slate-800 bg-opacity-40">
              <!-- Empty state -->
              <tr v-if="displayOrders.length === 0">
                <td :colspan="editMode ? 9 : 8" class="py-16 text-center">
                  <div class="flex flex-col items-center gap-3">
                    <div class="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-600 bg-slate-700 bg-opacity-50">
                      <ClipboardDocumentListIcon class="h-6 w-6 text-slate-400" />
                    </div>
                    <div>
                      <div class="text-sm font-semibold text-slate-300">Brak zamówień</div>
                      <div class="mt-1 text-xs text-slate-500">Ta trasa nie ma przypisanych zamówień.</div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr
                v-for="(ord, index) in displayOrders"
                :key="ord.id || index"
                class="transition hover:bg-slate-700 hover:bg-opacity-30"
                :draggable="editMode"
                @dragstart="onDragStart(index, $event)"
                @dragover.prevent="onDragOver(index, $event)"
                @dragleave="onDragLeave($event)"
                @drop="onDrop(index, $event)"
                :class="{ 'cursor-grab': editMode, 'dragOver': editMode && dragOverIndex === index }"
              >
                <!-- Order controls + delete in edit mode -->
                <td v-if="editMode" class="py-4 pl-3 pr-1 text-center">
                  <div class="flex flex-col items-center gap-1">
                    <button
                      type="button"
                      @click="moveOrder(index, -1)"
                      :disabled="index === 0"
                      class="orderMoveBtn"
                      :class="{ 'opacity-30 cursor-not-allowed': index === 0 }"
                    >
                      <ChevronUpIcon class="h-3.5 w-3.5" />
                    </button>
                    <span class="text-xs font-bold text-slate-400">{{ index + 1 }}</span>
                    <button
                      type="button"
                      @click="moveOrder(index, 1)"
                      :disabled="index === displayOrders.length - 1"
                      class="orderMoveBtn"
                      :class="{ 'opacity-30 cursor-not-allowed': index === displayOrders.length - 1 }"
                    >
                      <ChevronDownIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      @click="requestRemoveFromEdit(index)"
                      :disabled="ord.freshSnapshot?.status === 5 || ord.undelivered"
                      v-tooltip="ord.freshSnapshot?.status === 5 ? 'Nie można usunąć dostarczonego zamówienia' : (ord.undelivered ? 'Nie można usunąć niedostarczonego zamówienia' : 'Usuń z trasy')"
                      :class="(ord.freshSnapshot?.status === 5 || ord.undelivered)
                        ? 'orderMoveBtn mt-0.5 opacity-30 cursor-not-allowed'
                        : 'orderMoveBtn mt-0.5 border-red-500 border-opacity-40 text-red-400 hover:bg-red-500 hover:bg-opacity-20'"
                    >
                      <TrashIcon class="h-3 w-3" />
                    </button>
                  </div>
                </td>

                <!-- City -->
                <td class="whitespace-nowrap py-4 pl-5 pr-3 text-xs font-medium text-slate-100">
                  <div class="inline-flex items-center gap-2">
                    <div
                      v-if="ord.freshSnapshot === null || ord.freshSnapshot?.status === 6"
                      v-tooltip="ord.freshSnapshot === null
                        ? 'Uwaga, zamówienie zostało usunięte z systemu'
                        : ord.freshSnapshot.status === 6
                        ? 'Uwaga zamówienie zostało anulowane'
                        : 'Nieznany błąd'"
                      class="inline-flex items-center justify-center rounded-lg border border-red-500 border-opacity-30 bg-red-500 bg-opacity-10 p-1"
                    >
                      <ExclamationTriangleIcon class="h-3.5 w-3.5 text-red-300" />
                    </div>
                    <span>
                      {{ ord.freshSnapshot != null
                        ? ord.freshSnapshot.deliveryAddress.postCode + ' ' + ord.freshSnapshot.deliveryAddress.city
                        : ord.snapshotAtAddingTime.deliveryAddress.postCode + ' ' + ord.snapshotAtAddingTime.deliveryAddress.city
                      }}
                    </span>
                  </div>
                </td>

                <!-- Address -->
                <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-200">
                  <div class="inline-flex items-center gap-2">
                    <span>{{ ord.freshSnapshot != null
                      ? ord.freshSnapshot.deliveryAddress.address
                      : ord.snapshotAtAddingTime.deliveryAddress.address
                    }}</span>
                    <button
                      type="button"
                      v-tooltip="'Pokaż adres na mapie'"
                      class="inline-flex items-center justify-center h-6 w-6 rounded-lg border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-300 transition hover:bg-slate-600 hover:text-white"
                      @click="openMapPreview(ord.freshSnapshot != null ? ord.freshSnapshot.deliveryAddress : ord.snapshotAtAddingTime.deliveryAddress)"
                    >
                      <EyeIcon class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>

                <!-- Client -->
                <td class="px-3 py-4 text-xs text-slate-200">
                  <div class="max-w-[160px] truncate">
                    {{ ord.freshSnapshot
                      ? ord.freshSnapshot.client.invoiceAddress.name
                      : ord.snapshotAtAddingTime.companyName
                    }}
                  </div>
                </td>

                <!-- Product -->
                <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-200">
                  {{ ord.freshSnapshot != null
                    ? ord.freshSnapshot.orderedFuel.fuelName
                    : ord.snapshotAtAddingTime.orderedFuel.fuelName
                  }}
                </td>

                <!-- Qty -->
                <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-100">
                  <template v-if="ord.freshSnapshot?.unloadedFuel">
                    <div class="font-semibold text-green-300">{{ ord.freshSnapshot.unloadedFuel.quantityLiters }} L</div>
                    <div class="text-slate-500 text-[10px]">Zamówiono: {{ ord.freshSnapshot.orderedFuel.quantityLiters }} L</div>
                  </template>
                  <template v-else>
                    <span class="font-semibold">
                      {{ ord.freshSnapshot != null
                        ? ord.freshSnapshot.orderedFuel.quantityLiters + ' L'
                        : ord.snapshotAtAddingTime.orderedFuel.quantityLiters + ' L'
                      }}
                    </span>
                  </template>
                </td>

                <!-- Order Status -->
                <td class="whitespace-nowrap px-3 py-4 text-xs">
                  <!-- Niedostarczone (znacznik w trasie) -->
                  <span
                    v-if="ord.undelivered"
                    class="inline-flex items-center rounded-xl border border-orange-500 border-opacity-30 bg-orange-500 bg-opacity-10 px-2.5 py-1 text-xs font-semibold text-orange-300"
                  >
                    Niedostarczone
                  </span>
                  <template v-else-if="ord.freshSnapshot != null">
                    <!-- W trakcie realizacji + trasa aktywna → przyciski akcji -->
                    <div v-if="ord.freshSnapshot.status === 4 && route.status === 2" class="flex items-center gap-1.5">
                      <button
                        type="button"
                        @click="openUnloadModal(ord)"
                        v-tooltip="'Oznacz jako dostarczone i podaj ilość rozładowanego paliwa'"
                        class="inline-flex items-center gap-1.5 rounded-xl bg-green-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-green-500"
                      >
                        <CheckCircleIcon class="h-3.5 w-3.5" />
                        Rozładuj
                      </button>
                      <button
                        type="button"
                        @click="requestMarkUndelivered(ord)"
                        v-tooltip="'Oznacz jako niedostarczone — zamówienie wróci do puli'"
                        class="inline-flex items-center gap-1 rounded-xl border border-slate-600 bg-slate-700 px-2 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-600"
                      >
                        <XMarkIcon class="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <!-- Dostarczone -->
                    <span
                      v-else-if="ord.freshSnapshot.status === 5"
                      class="inline-flex items-center rounded-xl border border-green-500 border-opacity-30 bg-green-500 bg-opacity-10 px-2.5 py-1 text-xs font-semibold text-green-200"
                    >
                      Dostarczone
                    </span>
                    <!-- Inne statusy -->
                    <span
                      v-else
                      class="inline-flex items-center rounded-xl border border-slate-600 bg-slate-700 px-2.5 py-1 text-xs font-semibold text-slate-300"
                    >
                      {{ fuelOrderStatusLabel(ord.freshSnapshot.status) }}
                    </span>
                  </template>
                  <span v-else class="text-slate-500">—</span>
                </td>

                <!-- SENT status — tooltip pokazuje powód dla BUSINESS_ERROR. -->
                <td class="px-3 py-4 text-xs">
                  <span
                    v-tooltip="getSentTooltip(_orderId(ord))"
                    :class="['block max-w-full truncate rounded-xl px-2.5 py-1 text-left text-xs font-semibold', getSentColor(_orderId(ord))]"
                  >
                    {{ getSentLabel(_orderId(ord)) }}
                  </span>
                </td>

                <!-- Invoice -->
                <td class="whitespace-nowrap px-3 py-4 text-xs">
                  <!-- Niedostarczone — nie dotyczy -->
                  <span v-if="ord.undelivered" class="inline-flex items-center rounded-xl border border-slate-600 bg-slate-700 px-2.5 py-1 text-xs font-semibold text-slate-500">
                    Nie dotyczy
                  </span>
                  <template v-else-if="ord.freshSnapshot != null">
                    <!-- Zafakturowano -->
                    <span v-if="ord.freshSnapshot.invoice?.invoiced" class="inline-flex items-center rounded-xl border border-green-500 border-opacity-30 bg-green-500 bg-opacity-10 px-2.5 py-1 text-xs font-semibold text-green-200">
                      Zafakturowano
                    </span>
                    <!-- Dostarczone + nie zafakturowane → przycisk Wystaw -->
                    <button
                      v-else-if="ord.freshSnapshot.status === 5 && !ord.freshSnapshot.invoice?.invoiced"
                      type="button"
                      @click="generateInvoiceForOrder(ord)"
                      :disabled="invoiceGenerating === (ord.id || (ord.ref && ord.ref.id))"
                      class="inline-flex items-center gap-1.5 rounded-xl border border-blue-500 border-opacity-30 bg-blue-500 bg-opacity-10 px-2.5 py-1.5 text-xs font-semibold text-blue-300 transition hover:bg-opacity-20"
                    >
                      {{ invoiceGenerating === (ord.id || (ord.ref && ord.ref.id)) ? 'Wystawianie...' : 'Wystaw FV' }}
                    </button>
                    <!-- Zafakturowane -->
                    <span
                      v-else-if="ord.freshSnapshot.status === 5 && ord.freshSnapshot.invoice?.invoiced"
                      class="inline-flex items-center rounded-xl border border-green-500 border-opacity-30 bg-green-500 bg-opacity-10 px-2.5 py-1 text-xs font-semibold text-green-200"
                    >
                      {{ ord.freshSnapshot.invoice.invoiceNumber || 'Zafakturowano' }}
                    </span>
                    <!-- Inne — oczekuje -->
                    <span v-else class="inline-flex items-center rounded-xl border border-slate-600 bg-slate-700 px-2.5 py-1 text-xs font-semibold text-slate-300">
                      Oczekuje
                    </span>
                  </template>
                  <span v-else>
                    <span class="inline-flex items-center rounded-xl border border-red-500 border-opacity-30 bg-red-500 bg-opacity-10 px-2.5 py-1 text-xs font-semibold text-red-300">
                      Brak danych
                    </span>
                  </span>
                </td>

                <!-- Files -->
                <td class="whitespace-nowrap px-3 py-4 text-xs">
                  <div class="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      @click="downloadWaybillPdf(ord)"
                      v-tooltip="'List przewozowy'"
                      class="fileBtn fileBtnTeal"
                    >
                      <ClipboardDocumentListIcon class="h-3.5 w-3.5" />
                    </button>
                    <!-- Wyślij SENT_105 — widoczne tylko gdy zamówienie jeszcze nie ma wysyłki. -->
                    <button
                      v-if="!_orderHasSent(ord)"
                      type="button"
                      :disabled="sent.loadingByOrderId[_orderId(ord)]"
                      @click="sendSentForOrder(ord)"
                      v-tooltip="'Wyślij SENT_105'"
                      class="fileBtn fileBtnBlue"
                    >
                      <DocumentTextIcon class="h-3.5 w-3.5" />
                    </button>
                    <!-- Historia SENT — widoczna po wysłaniu. -->
                    <button
                      v-if="_orderHasSent(ord)"
                      type="button"
                      @click="openSentHistory(ord)"
                      v-tooltip="'Historia SENT (komunikaty PUESC)'"
                      class="fileBtn fileBtnBlue"
                    >
                      <ClockIcon class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Capacity footer -->
        <div class="border-t border-slate-700 px-5 py-4">
          <div class="mb-2 flex items-center justify-between gap-4">
            <span class="text-xs font-semibold text-slate-400">Wykorzystanie pojemności</span>
            <div class="flex items-center gap-3">
              <span
                class="text-xs font-semibold"
                :class="capacityPercent >= 100 ? 'text-red-300' : capacityPercent >= 90 ? 'text-yellow-300' : 'text-green-300'"
              >
                {{ capacityLabel }}
              </span>
              <span class="text-xs font-semibold text-slate-400">{{ capacityPercent }}%</span>
            </div>
          </div>
          <div class="h-2 w-full overflow-hidden rounded-full bg-slate-700">
            <div
              class="h-full rounded-full transition-all duration-700"
              :class="capacityPercent >= 100 ? 'bg-red-500' : capacityPercent >= 90 ? 'bg-yellow-500' : 'bg-green-500'"
              :style="{ width: Math.min(capacityPercent, 100) + '%' }"
            />
          </div>
        </div>

      </div>
    </div>

    <!-- MODAL: Potwierdź dostawę -->
    <Teleport to="body">
    <div v-if="unloadModal.show" class="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" @click="unloadModal.show = false" />
      <div class="relative z-10 w-full max-w-md border border-slate-700 bg-slate-800 shadow-2xl" style="border-radius: 22px;">
        <div class="p-5 space-y-4">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-green-500 border-opacity-30 bg-green-500 bg-opacity-10">
              <CheckCircleIcon class="h-5 w-5 text-green-400" />
            </div>
            <div>
              <div class="text-sm font-semibold text-white">Potwierdź dostawę</div>
              <div class="text-xs text-slate-400">Podaj ilość rozładowanego paliwa</div>
            </div>
          </div>

          <div v-if="unloadModal.order" class="space-y-2">
            <div class="softBox">
              <div class="softBoxLabel">Klient</div>
              <div class="softBoxValue text-blue-300">{{ unloadModal.order.client?.invoiceAddress?.name || '—' }}</div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="softBox">
                <div class="softBoxLabel">Produkt</div>
                <div class="softBoxValue">{{ unloadModal.order.orderedFuel?.fuelName || '—' }}</div>
              </div>
              <div class="softBox">
                <div class="softBoxLabel">Zamówiono</div>
                <div class="softBoxValue">{{ unloadModal.order.orderedFuel?.quantityLiters || 0 }} L</div>
              </div>
            </div>
          </div>

          <div class="fieldWrap">
            <label class="fieldLabel">Ilość rozładowanego paliwa (L)*</label>
            <input
              v-model="unloadModal.quantityInput"
              @input="sanitizeUnloadInput()"
              type="text"
              inputmode="decimal"
              class="fieldInput"
              placeholder="np. 950.50"
            />
          </div>

          <div class="flex items-center justify-end gap-2 pt-2">
            <button type="button" @click="unloadModal.show = false" class="btnSecondary">Anuluj</button>
            <button
              type="button"
              @click="confirmUnload()"
              :disabled="unloadModal.saving"
              class="inline-flex items-center gap-2 rounded-xl border border-green-500 border-opacity-40 bg-green-500 bg-opacity-15 px-4 py-2 text-sm font-medium text-green-300 transition hover:bg-opacity-25"
            >
              <span v-if="unloadModal.saving">Zapisywanie...</span>
              <span v-else>Potwierdź dostawę</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- MODAL: Potwierdź generowanie faktur -->
    <Teleport to="body">
    <div v-if="invoiceConfirm.show" class="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" @click="invoiceConfirm.show = false" />
      <div class="relative z-10 w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-800 shadow-2xl">
        <div class="flex items-center justify-between border-b border-slate-700 px-5 py-4">
          <div class="text-sm font-semibold text-white">{{ invoiceConfirm.title }}</div>
          <button type="button" @click="invoiceConfirm.show = false" class="rounded-xl border border-slate-600 bg-slate-700 p-2 text-slate-300 hover:bg-slate-600">
            <XMarkIcon class="h-4 w-4" />
          </button>
        </div>
        <div class="px-5 py-4 text-sm text-slate-300">
          <p>{{ invoiceConfirm.message }}</p>
        </div>
        <div class="flex items-center justify-end gap-2 border-t border-slate-700 px-5 py-4">
          <button type="button" @click="invoiceConfirm.show = false" class="btnSecondary">Anuluj</button>
          <button type="button" @click="invoiceConfirm.onConfirm(); invoiceConfirm.show = false" class="btnPrimary">
            {{ invoiceConfirm.confirmText }}
          </button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- MODAL: Potwierdź niedostarczone -->
    <Teleport to="body">
    <div v-if="undeliveredConfirm.show" class="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" @click="undeliveredConfirm.show = false" />
      <div class="relative z-10 w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-800 shadow-2xl">
        <div class="flex items-center justify-between border-b border-slate-700 px-5 py-4">
          <div class="text-sm font-semibold text-white">Oznacz jako niedostarczone?</div>
          <button type="button" @click="undeliveredConfirm.show = false" class="rounded-xl border border-slate-600 bg-slate-700 p-2 text-slate-300 hover:bg-slate-600">
            <XMarkIcon class="h-4 w-4" />
          </button>
        </div>
        <div class="px-5 py-4 text-sm text-slate-300 space-y-1">
          <p>Zamówienie wróci do puli i będzie można je przypisać do innej trasy.</p>
          <p class="text-slate-500 text-xs">W historii trasy pozostanie znacznik próby dostarczenia.</p>
        </div>
        <div class="flex items-center justify-end gap-2 border-t border-slate-700 px-5 py-4">
          <button type="button" @click="undeliveredConfirm.show = false" class="btnSecondary">Anuluj</button>
          <button type="button" @click="markOrderUndelivered(undeliveredConfirm.ord); undeliveredConfirm.show = false" class="btnDanger">
            Oznacz jako niedostarczone
          </button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- MODAL: Dodaj punkty do trasy -->
    <Teleport to="body">
    <div v-if="addOrdersModal.show" class="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" @click="closeAddOrdersModal()" />
      <div class="relative z-10 w-full max-w-4xl rounded-2xl border border-slate-700 bg-slate-800 shadow-2xl">
        <!-- Modal header -->
        <div class="flex items-center justify-between border-b border-slate-700 px-5 py-4">
          <div>
            <div class="text-sm font-semibold text-white">Dodaj punkty do trasy</div>
            <div class="mt-0.5 text-xs text-slate-400">Wybierz zamówienia do dodania</div>
          </div>
          <button type="button" @click="closeAddOrdersModal()" class="rounded-xl border border-slate-600 bg-slate-700 p-2 text-slate-300 hover:bg-slate-600">
            <XMarkIcon class="h-4 w-4" />
          </button>
        </div>

        <!-- Filter -->
        <div class="flex items-center gap-2 border-b border-slate-700 px-5 py-3">
          <label class="shrink-0 text-xs font-semibold text-slate-400">Region:</label>
          <select v-model="addOrdersModal.filterRegion" @change="fetchAddOrders()" class="filterFieldInline">
            <option value="all">Wszystkie regiony</option>
            <option v-for="region in regions.all" :key="region.id" :value="region.name">{{ region.name }}</option>
          </select>
          <button v-if="addOrdersModal.filterRegion !== 'all'" type="button" @click="addOrdersModal.filterRegion = 'all'; fetchAddOrders()" class="btnGhostXs">
            <XMarkIcon class="h-3.5 w-3.5" />Resetuj
          </button>
        </div>

        <!-- Table -->
        <div class="max-h-[50vh] overflow-y-auto px-5 py-4 space-y-4">

          <!-- Removed orders section (re-add) -->
          <div v-if="addOrdersModal.removedOrdersList.length > 0">
            <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-yellow-400">Odpięte z tej trasy — możesz przywrócić</div>
            <div class="overflow-hidden rounded-2xl border border-yellow-500 border-opacity-30">
              <table class="w-full divide-y divide-slate-700">
                <tbody class="divide-y divide-slate-700 bg-yellow-500 bg-opacity-5">
                  <tr v-for="row in addOrdersModal.removedOrdersList" :key="'rem-'+row.id" class="transition hover:bg-slate-700 hover:bg-opacity-30">
                    <td class="px-4 py-3 text-xs text-slate-100">{{ row.snapshotAtAddingTime?.companyName || '—' }}</td>
                    <td class="px-3 py-3 text-xs text-slate-200">{{ row.snapshotAtAddingTime?.deliveryAddress?.regionName || '—' }}</td>
                    <td class="px-3 py-3 text-xs text-slate-200">{{ row.snapshotAtAddingTime?.deliveryAddress?.postCode }} {{ row.snapshotAtAddingTime?.deliveryAddress?.city }}</td>
                    <td class="px-3 py-3 text-xs text-slate-200">{{ row.snapshotAtAddingTime?.orderedFuel?.fuelName || '—' }}</td>
                    <td class="px-3 py-3 text-xs text-slate-200">{{ row.snapshotAtAddingTime?.orderedFuel?.quantityLiters }} L</td>
                    <td class="py-3 pl-3 pr-4 text-right">
                      <input
                        :checked="addOrdersModal.checkedIds.includes(row.id)"
                        @change="toggleAddOrder(row)"
                        type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Firestore orders -->
          <div>
            <div v-if="addOrdersModal.removedOrdersList.length > 0" class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Dostępne zamówienia</div>
            <div v-if="addOrdersModal.loading" class="flex items-center justify-center py-10">
              <div class="spinner"></div>
            </div>
            <div v-else class="overflow-hidden rounded-2xl border border-slate-700">
              <div class="w-full overflow-x-auto">
                <table class="min-w-[600px] w-full divide-y divide-slate-700">
                  <thead class="bg-slate-700 bg-opacity-70">
                    <tr>
                      <th class="py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase text-slate-300">Klient</th>
                      <th class="px-3 py-3 text-left text-xs font-semibold uppercase text-slate-300">Region</th>
                      <th class="px-3 py-3 text-left text-xs font-semibold uppercase text-slate-300">Adres</th>
                      <th class="px-3 py-3 text-left text-xs font-semibold uppercase text-slate-300">Produkt</th>
                      <th class="px-3 py-3 text-left text-xs font-semibold uppercase text-slate-300">Ilość</th>
                      <th class="py-3 pl-3 pr-4 text-right text-xs font-semibold uppercase text-slate-300">Wybór</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-700 bg-slate-800 bg-opacity-40">
                    <tr v-for="row in addOrdersModal.orders" :key="row.id" class="transition hover:bg-slate-700 hover:bg-opacity-30">
                      <td class="px-4 py-3 text-xs text-slate-100">
                        <div class="flex items-center gap-2">
                          <span>{{ row.client?.invoiceAddress?.name || '—' }}</span>
                          <span
                            v-if="row._isReactivation"
                            v-tooltip="'Zamówienie jest już w tej trasie jako niedostarczone — zaznaczenie spowoduje jego ponowne aktywowanie'"
                            class="inline-flex items-center rounded-lg border border-orange-500 border-opacity-40 bg-orange-500 bg-opacity-10 px-1.5 py-0.5 text-[10px] font-semibold text-orange-300"
                          >
                            Reaktywacja
                          </span>
                        </div>
                      </td>
                      <td class="px-3 py-3 text-xs text-slate-200">
                        <div class="inline-flex items-center gap-2">
                          <span>{{ row.deliveryAddress?.regionName || '—' }}</span>
                          <button
                            type="button"
                            v-tooltip="'Pokaż adres dostawy na mapie'"
                            class="inline-flex items-center justify-center h-6 w-6 rounded-lg border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-300 transition hover:bg-slate-600 hover:text-white"
                            @click="openMapPreview(row.deliveryAddress)"
                          >
                            <EyeIcon class="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                      <td class="px-3 py-3 text-xs text-slate-200">{{ row.deliveryAddress?.postCode }} {{ row.deliveryAddress?.city }}</td>
                      <td class="px-3 py-3 text-xs text-slate-200">{{ row.orderedFuel?.fuelName || '—' }}</td>
                      <td class="px-3 py-3 text-xs text-slate-200">{{ row.orderedFuel?.quantityLiters }} L</td>
                      <td class="py-3 pl-3 pr-4 text-right">
                        <input
                          :checked="addOrdersModal.checkedIds.includes(row.id)"
                          @change="toggleAddOrder(row)"
                          type="checkbox"
                          class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <EmptyState v-if="addOrdersModal.orders.length === 0" />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between border-t border-slate-700 px-5 py-4">
          <span class="text-xs text-slate-400">Wybrano: {{ addOrdersModal.checkedIds.length }} zamówień</span>
          <div class="flex items-center gap-2">
            <button type="button" @click="closeAddOrdersModal()" class="btnSecondary">Anuluj</button>
            <button type="button" @click="confirmAddOrders()" :disabled="addOrdersModal.checkedIds.length === 0" :class="['btnPrimary', addOrdersModal.checkedIds.length === 0 ? 'opacity-50 cursor-not-allowed' : '']">
              Dodaj wybrane
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- SENT guard: ostrzeżenie przed "Oznacz jako niedostarczone" gdy zamówienie wysłane do PUESC -->
    <Teleport to="body">
    <div v-if="sentUndeliveredConfirm.show" class="fixed inset-0 z-[1000] flex items-center justify-center p-6">
      <div class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" @click="sentUndeliveredConfirm = { show: false, ord: null }" />
      <div class="relative w-full max-w-md rounded-3xl border border-yellow-500 border-opacity-40 bg-slate-800 shadow-2xl" style="border-radius: 28px;">
        <div class="flex items-start justify-between gap-4 border-b border-slate-700 p-5">
          <div class="flex items-center gap-3">
            <ExclamationTriangleIcon class="h-6 w-6 text-yellow-400" />
            <h3 class="text-base font-semibold text-white">Uwaga — zamówienie wysłane do PUESC</h3>
          </div>
          <button type="button" @click="sentUndeliveredConfirm = { show: false, ord: null }" class="rounded-xl border border-slate-600 bg-slate-700 p-2 text-slate-300 hover:bg-slate-600">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
        <div class="p-5 text-sm text-slate-200">
          To zamówienie zostało wysłane do PUESC jako zgłoszenie SENT.
          <strong class="text-yellow-300">Brak dostarczenia musisz zgłosić w portalu PUESC samodzielnie</strong>
          poza tą aplikacją (zmiana zgłoszenia / anulowanie). Czy rozumiesz?
        </div>
        <div class="flex justify-end gap-3 border-t border-slate-700 p-5">
          <button type="button" @click="sentUndeliveredConfirm = { show: false, ord: null }" class="btnSecondary">Anuluj</button>
          <button type="button" @click="confirmSentUndelivered()" class="btnDanger">
            Rozumiem, kontynuuj
          </button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- SENT guard: ostrzeżenie przed "Usuń z trasy" gdy zamówienie wysłane do PUESC -->
    <Teleport to="body">
    <div v-if="sentRemoveFromRouteConfirm.show" class="fixed inset-0 z-[1000] flex items-center justify-center p-6">
      <div class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" @click="sentRemoveFromRouteConfirm = { show: false, indexToRemove: -1, orderId: '' }" />
      <div class="relative w-full max-w-md rounded-3xl border border-yellow-500 border-opacity-40 bg-slate-800 shadow-2xl" style="border-radius: 28px;">
        <div class="flex items-start justify-between gap-4 border-b border-slate-700 p-5">
          <div class="flex items-center gap-3">
            <ExclamationTriangleIcon class="h-6 w-6 text-yellow-400" />
            <h3 class="text-base font-semibold text-white">Uwaga — zamówienie wysłane do PUESC</h3>
          </div>
          <button type="button" @click="sentRemoveFromRouteConfirm = { show: false, indexToRemove: -1, orderId: '' }" class="rounded-xl border border-slate-600 bg-slate-700 p-2 text-slate-300 hover:bg-slate-600">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
        <div class="p-5 text-sm text-slate-200">
          Zamówienie <strong class="text-white">{{ sentRemoveFromRouteConfirm.orderId }}</strong>
          zostało wysłane do PUESC jako zgłoszenie SENT.
          <strong class="text-yellow-300">Po usunięciu z trasy musisz zgłosić zmianę do PUESC samodzielnie</strong>
          (anulowanie / aktualizacja zgłoszenia w portalu). Czy rozumiesz?
        </div>
        <div class="flex justify-end gap-3 border-t border-slate-700 p-5">
          <button type="button" @click="sentRemoveFromRouteConfirm = { show: false, indexToRemove: -1, orderId: '' }" class="btnSecondary">Anuluj</button>
          <button type="button" @click="confirmSentRemoveFromRoute()" class="btnDanger">
            Rozumiem, usuń z trasy
          </button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- SENT history: lista komunikatów PUESC (SENT_115/500/112/UPP/NPP) z możliwością pobrania. -->
    <Teleport to="body">
    <div v-if="sentHistoryModal.show" class="fixed inset-0 z-[1000] flex items-center justify-center p-6">
      <div class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" @click="closeSentHistory()" />
      <div class="relative w-full max-w-3xl rounded-3xl border border-slate-700 bg-slate-800 shadow-2xl" style="border-radius: 28px;">
        <div class="flex items-start justify-between gap-4 border-b border-slate-700 p-5">
          <div class="flex items-center gap-3">
            <ClockIcon class="h-6 w-6 text-blue-300" />
            <div>
              <h3 class="text-base font-semibold text-white">Historia SENT</h3>
              <div class="text-xs text-slate-400">Zamówienie {{ sentHistoryModal.orderId || '—' }}</div>
            </div>
          </div>
          <button type="button" @click="closeSentHistory()" class="rounded-xl border border-slate-600 bg-slate-700 p-2 text-slate-300 hover:bg-slate-600">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
        <div class="p-5 space-y-4">
          <!-- Banner: PUESC limituje pobieranie (dark slate + yellow text → kontrast OK na bg-slate-800). -->
          <div v-if="sentHistoryModal.cronHealth?.rateLimited" class="rounded-xl border border-yellow-500 border-opacity-50 bg-yellow-500 bg-opacity-10 p-3 text-xs text-yellow-200">
            <p class="font-semibold text-yellow-100">PUESC chwilowo limituje pobieranie.</p>
            <p class="mt-1">Komunikaty pojawią się tutaj automatycznie po następnym cyklu (do 10 min).</p>
          </div>
          <!-- Banner: cron zamarł -->
          <div v-else-if="sentHistoryModal.cronHealth?.stale" class="rounded-xl border border-red-500 border-opacity-50 bg-red-500 bg-opacity-10 p-3 text-xs text-red-200">
            <p class="font-semibold text-red-100">System pobierania nieaktywny.</p>
            <p class="mt-1">Ostatnie sprawdzenie {{ sentHistoryModal.cronHealth.minutesSinceLastRun }} min temu. Skontaktuj się z opiekunem systemu.</p>
          </div>

          <!-- Banner: BUSINESS_ERROR — czytelny opis błędu walidacji + retry button od razu na górze. -->
          <div v-if="sentHistoryModal.dbStatus === 'BUSINESS_ERROR' && sentHistoryModal.errorTranslation" class="rounded-xl border border-red-500 border-opacity-50 bg-red-500 bg-opacity-10 p-4 text-sm text-red-100">
            <p class="font-semibold text-red-100">Zgłoszenie odrzucone przez PUESC</p>
            <p class="mt-1.5 text-xs text-red-200">{{ sentHistoryModal.errorTranslation }}</p>
            <details v-if="sentHistoryModal.errorRaw" class="mt-2">
              <summary class="cursor-pointer text-[11px] text-red-300 hover:text-red-200">Pokaż techniczny opis</summary>
              <p class="mt-1 whitespace-pre-wrap text-[11px] text-red-300 font-mono">{{ sentHistoryModal.errorRaw }}</p>
            </details>
            <button
              type="button"
              :disabled="sentHistoryModal.retrying"
              @click="retrySentFromHistory()"
              class="mt-3 w-full rounded-xl border border-blue-500 border-opacity-50 bg-blue-500 bg-opacity-15 px-4 py-2.5 text-sm font-semibold text-blue-100 transition hover:bg-opacity-25 disabled:opacity-50"
            >
              {{ sentHistoryModal.retrying ? 'Wysyłam ponownie…' : 'Wyślij SENT_105 ponownie' }}
            </button>
          </div>

          <div v-if="sentHistoryModal.loading" class="py-10 text-center text-sm text-slate-400">
            Ładowanie historii...
          </div>
          <div v-else-if="sentHistoryModal.error" class="rounded-xl border border-red-500 border-opacity-40 bg-red-500 bg-opacity-10 p-4 text-sm text-red-200">
            {{ sentHistoryModal.error }}
          </div>
          <div v-else-if="sentHistoryModal.items.length === 0" class="space-y-3">
            <div class="rounded-xl border border-slate-700 bg-slate-900 bg-opacity-50 p-4 text-center text-sm text-slate-300">
              <p class="font-semibold text-slate-100">{{ sentHistoryModal.problemHint || 'Brak komunikatów PUESC.' }}</p>
              <p class="mt-2 text-[11px] text-slate-500">
                Status: <span class="font-semibold text-slate-300">{{ humanizeSentStatus(sentHistoryModal.dbStatus) }}</span>
                <span v-if="sentHistoryModal.minutesSinceSent != null"> · wysłane {{ formatMinutesAgo(sentHistoryModal.minutesSinceSent) }}</span>
              </p>
            </div>
            <button
              v-if="sentHistoryModal.canRetry"
              type="button"
              :disabled="sentHistoryModal.retrying"
              @click="retrySentFromHistory()"
              class="w-full rounded-xl border border-blue-500 border-opacity-50 bg-blue-500 bg-opacity-15 px-4 py-2.5 text-sm font-semibold text-blue-100 transition hover:bg-opacity-25 disabled:opacity-50"
            >
              {{ sentHistoryModal.retrying ? 'Wysyłam ponownie…' : 'Wyślij SENT_105 ponownie' }}
            </button>
          </div>
          <div v-else class="overflow-hidden rounded-2xl border border-slate-700">
            <table class="w-full divide-y divide-slate-700">
              <thead class="bg-slate-700 bg-opacity-70">
                <tr>
                  <th class="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-200">Data</th>
                  <th class="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-200">Typ</th>
                  <th class="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-200">Format</th>
                  <th class="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-200">Plik</th>
                  <th class="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-200"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700 bg-slate-900 bg-opacity-30">
                <tr v-for="item in sentHistoryModal.items" :key="item.idx">
                  <td class="whitespace-nowrap px-3 py-3 text-xs text-slate-200">{{ formatSentReceivedAt(item.receivedAt) }}</td>
                  <td class="whitespace-nowrap px-3 py-3 text-xs font-semibold text-blue-200">{{ item.messageType || '—' }}</td>
                  <td class="whitespace-nowrap px-3 py-3 text-xs text-slate-300">{{ formatSentMime(item.mime) }}</td>
                  <td class="px-3 py-3 text-xs text-slate-400">{{ item.filename || '—' }}</td>
                  <td class="whitespace-nowrap px-3 py-3 text-right">
                    <button
                      type="button"
                      :disabled="sentHistoryModal.downloadingIdx === item.idx"
                      @click="downloadSentMessage({ ...item, documentId: sentHistoryModal.orderId })"
                      class="inline-flex items-center gap-1.5 rounded-xl border border-slate-600 bg-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-100 transition hover:bg-slate-600 disabled:opacity-50"
                    >
                      <ArrowDownTrayIconOutline class="h-3.5 w-3.5" />
                      {{ sentHistoryModal.downloadingIdx === item.idx ? 'Pobieram...' : 'Pobierz' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="flex justify-end gap-3 border-t border-slate-700 p-5">
          <button type="button" @click="closeSentHistory()" class="btnSecondary">Zamknij</button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Map preview modal (shared) -->
    <ModalMapPreview
      :show="mapPreviewModal.show"
      :delivery-address="mapPreviewModal.deliveryAddress"
      @close="closeMapPreview"
    />

  </div>
</template>

<script>
    import {db} from "@/firebase/gfbconf.js"
    import axios from 'axios'
    import { createSentApi, buildSent105FromOrder } from '@/services/sentApi'
    import { getSentLabel, getSentColor, getSentTooltip } from '@/utils/sentStatus'
    import {
      HomeIcon,
      ChevronDownIcon,
      ChevronUpIcon,
      CheckIcon,
      PlusIcon,
      ArrowDownTrayIcon,
      ArrowTopRightOnSquareIcon,
      DocumentTextIcon,
      ClipboardDocumentListIcon,
      ChevronRightIcon,
      TrashIcon,
      CheckCircleIcon,
    } from '@heroicons/vue/24/solid'
    import {
      ExclamationTriangleIcon,
      XMarkIcon,
      FunnelIcon,
      ArrowLeftIcon,
      MapPinIcon,
      UserIcon,
      TruckIcon,
      FlagIcon,
      PencilSquareIcon,
      NoSymbolIcon,
      CalendarDaysIcon,
      EyeIcon,
      ClockIcon,
      ArrowDownTrayIcon as ArrowDownTrayIconOutline,
    } from '@heroicons/vue/24/outline'
    import { ListboxOptions, ListboxOption, ListboxButton, Listbox } from '@headlessui/vue'
    import VueDatePicker from '@vuepic/vue-datepicker';
    import EmptyState from '@/components/EmptyState.vue'
    import ModalMapPreview from '@/components/ModalMapPreview.vue'
    import moment, { unix } from 'moment'
    import '@vuepic/vue-datepicker/dist/main.css'
    import pdfMake from "pdfmake/build/pdfmake";
    import pdfFonts from "pdfmake/build/vfs_fonts";
    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    export default {
        name: "Template",
        data() {
            return {
                route: null,
                editMode: false,
                saving: false,
                cancelingRoute: false,
                showCancelModal: false,
                editData: {
                  name: '',
                  note: '',
                  orders: [],
                  removedOrders: [],
                  originalOrderIds: [],
                },
                invoiceGenerating: null,
                generatingAllInvoices: false,
                invoiceConfirm: {
                  show: false,
                  title: '',
                  message: '',
                  confirmText: '',
                  onConfirm: () => {}
                },
                unloadModal: {
                  show: false,
                  order: null,
                  orderId: null,
                  quantityInput: '',
                  saving: false
                },
                undeliveredConfirm: {
                  show: false,
                  ord: null
                },
                editDate: null,
                editSelectedDriver: null,
                editSelectedRegion: null,
                editSelectedVehicles: null,
                editSelectedTrails: null,
                drivers: { all: [] },
                regions: { all: [] },
                vehicles: [],
                trails: [],
                addOrdersModal: {
                  show: false,
                  loading: false,
                  orders: [],
                  removedOrdersList: [],
                  checkedIds: [],
                  selectedById: {},
                  listener: null,
                  limitReached: false,
                  filterRegion: 'all',
                },
                dragIndex: null,
                dragOverIndex: null,
                mapPreviewModal: {
                  show: false,
                  deliveryAddress: null
                },
                // SENT: cache statusów per orderId + flagi loading + modale ostrzegawcze
                sent: {
                  statuses: {},          // { [orderId]: payload.status z getSentStatus }
                  loadingByOrderId: {},  // { [orderId]: bool }
                  bulkLoading: false,
                },
                sentUndeliveredConfirm: { show: false, ord: null },
                sentRemoveFromRouteConfirm: { show: false, indexToRemove: -1, orderId: '' },
                sentHistoryModal: {
                  show: false,
                  orderId: '',
                  loading: false,
                  error: null,
                  items: [],
                  downloadingIdx: null,
                  // Diagnostyka (uzupełniana przy openSentHistory):
                  dbStatus: null,             // status z urzad-api (PENDING/BUSINESS_ERROR/...)
                  minutesSinceSent: null,
                  cronHealth: null,           // { rateLimited, stale, minutesSinceLastRun, ... }
                  problemHint: null,
                  canRetry: false,
                  retrying: false,
                  // BUSINESS_ERROR translation (czytelny tekst + raw NPP/SENT_500 do "pokaż techniczny opis").
                  errorTranslation: null,
                  errorRaw: null,
                  // Auto-refresh — interval id, czyszczony przy close.
                  refreshTimer: null,
                },
            }
        },
        components: {
            ClipboardDocumentListIcon,
            DocumentTextIcon,
            ArrowTopRightOnSquareIcon,
            PlusIcon,
            ArrowDownTrayIcon,
            EmptyState,
            FunnelIcon,
            HomeIcon,
            ChevronDownIcon,
            ChevronUpIcon,
            CheckIcon,
            ExclamationTriangleIcon,
            XMarkIcon,
            VueDatePicker,
            ChevronRightIcon,
            ArrowLeftIcon,
            MapPinIcon,
            UserIcon,
            TruckIcon,
            FlagIcon,
            PencilSquareIcon,
            TrashIcon,
            CheckCircleIcon,
            NoSymbolIcon,
            CalendarDaysIcon,
            EyeIcon,
            ClockIcon,
            ArrowDownTrayIconOutline,
            Listbox,
            ListboxButton,
            ListboxOption,
            ListboxOptions,
            ModalMapPreview,
        },
        created() {
            if(!this.$route.query.id) {
                this.$router.push('/dashboard/routes')
            }
            this.downloadRouteDetails()
        },
        mounted() {},
        beforeUnmount() {
          // Czyszczenie timera auto-refresh historii SENT — zapobiega memory leak po opuszczeniu widoku.
          if (this.sentHistoryModal?.refreshTimer) {
            clearInterval(this.sentHistoryModal.refreshTimer)
          }
        },
        watch: {},
        methods: {
          // ========== SENT integration ==========
          _sentApiClient() {
            const baseUrl = this.$store.state.apiComplaiaLink
            return createSentApi(baseUrl)
          },
          _orderId(ordEntry) {
            return ordEntry?.id || ordEntry?.ref?.id || ''
          },
          _orderHasSent(ord) {
            const snap = this._getSnapshot(ord)
            return !!(snap?.invoice?.sent?.documentId)
          },
          // Cache trzyma { status, errors } per orderId. Odczytujemy bezpiecznie.
          _sentEntry(orderId) {
            return this.sent.statuses[orderId] || null
          },
          getSentLabel(orderId) {
            return getSentLabel(this._sentEntry(orderId)?.status || null)
          },
          getSentColor(orderId) {
            return getSentColor(this._sentEntry(orderId)?.status || null)
          },
          getSentTooltip(orderId) {
            const e = this._sentEntry(orderId)
            return getSentTooltip(e?.status || null, e?.errors || [])
          },
          /** Pyta urzad-api o aktualny status zgłoszenia, cache w sent.statuses[orderId]. */
          async refreshSentStatusForOrder(orderId) {
            if (!orderId) return
            try {
              const api = this._sentApiClient()
              const res = await api.getSentStatus(orderId)
              if (res?.success && res?.payload?.status) {
                this.sent.statuses = {
                  ...this.sent.statuses,
                  [orderId]: { status: res.payload.status, errors: res.payload.errors || [] },
                }
              } else {
                this.sent.statuses = { ...this.sent.statuses, [orderId]: null }
              }
            } catch (err) {
              console.warn('[SENT] getSentStatus failed for', orderId, err?.message)
            }
          },
          /** Batch refresh dla wszystkich zamówień z invoice.sent.documentId. Maks 10 równolegle. */
          async refreshAllSentStatuses() {
            const orders = (this.route?.orders || [])
            const ids = orders
              .map(o => this._getSnapshot(o)?.invoice?.sent?.documentId)
              .filter(Boolean)
            // Batch po 10
            for (let i = 0; i < ids.length; i += 10) {
              const slice = ids.slice(i, i + 10)
              await Promise.allSettled(slice.map(id => this.refreshSentStatusForOrder(id)))
            }
          },
          /**
           * Otwórz modal historii SENT — listing komunikatów PUESC dla zamówienia.
           * Równolegle pobiera status z DB urzad-api + zdrowie crona pullInbox,
           * żeby admin nie zobaczył tylko "Brak komunikatów" bez kontekstu.
           * Auto-refresh co 30s dopóki modal otwarty.
           */
          async openSentHistory(ordEntry) {
            const orderId = this._orderId(ordEntry)
            const snap = this._getSnapshot(ordEntry)
            const documentId = snap?.invoice?.sent?.documentId
            const sentAtUnix = snap?.invoice?.sent?.sentAt?.unix
            const minutesSinceSent = sentAtUnix
              ? Math.floor((Math.floor(Date.now() / 1000) - sentAtUnix) / 60)
              : null
            this.sentHistoryModal = {
              show: true, orderId, loading: false, error: null, items: [], downloadingIdx: null,
              dbStatus: null, minutesSinceSent, cronHealth: null,
              problemHint: null, canRetry: false, retrying: false,
              errorTranslation: null, errorRaw: null,
              refreshTimer: null,
            }
            if (!documentId) {
              this.sentHistoryModal.error = 'Brak wysyłki SENT dla tego zamówienia.'
              return
            }
            this.sentHistoryModal.loading = true
            await this._refreshSentHistorySilent({ documentId })
            this.sentHistoryModal.loading = false
            // Auto-refresh co 30s — dopóki modal otwarty.
            this.sentHistoryModal.refreshTimer = setInterval(() => {
              if (!this.sentHistoryModal.show) return
              this._refreshSentHistorySilent({ documentId })
            }, 30000)
          },
          /** Pobranie aktualnej historii + statusu + cron-healtha bez resetowania UI loadera. */
          async _refreshSentHistorySilent({ documentId }) {
            const api = this._sentApiClient()
            const [msgsRes, statusRes, healthRes] = await Promise.allSettled([
              api.listMessages(documentId),
              api.getSentStatus(documentId),
              api.getCronHealth(),
            ])
            // Komunikaty
            if (msgsRes.status === 'fulfilled' && msgsRes.value?.success) {
              const list = Array.isArray(msgsRes.value?.payload?.messages) ? msgsRes.value.payload.messages.slice() : []
              list.sort((a, b) => new Date(b.receivedAt || 0) - new Date(a.receivedAt || 0))
              this.sentHistoryModal.items = list
            } else if (msgsRes.status === 'fulfilled' && this.sentHistoryModal.items.length === 0) {
              this.sentHistoryModal.error = msgsRes.value?.message || 'Nie udało się pobrać historii.'
            } else if (msgsRes.status === 'rejected' && this.sentHistoryModal.items.length === 0) {
              this.sentHistoryModal.error = msgsRes.reason?.message || 'Błąd sieci.'
            }
            // Status w DB
            if (statusRes.status === 'fulfilled' && statusRes.value?.success) {
              const payload = statusRes.value.payload || {}
              this.sentHistoryModal.dbStatus = payload.status || null
              // Tłumaczenie błędu PUESC dla BUSINESS_ERROR (xsd validation → polski).
              if (payload.status === 'BUSINESS_ERROR' && Array.isArray(payload.errors) && payload.errors.length) {
                const e0 = payload.errors[0]
                const raw = e0?.description || e0?.message || ''
                this.sentHistoryModal.errorRaw = raw
                this.sentHistoryModal.errorTranslation = this._translateNppError(raw)
              } else {
                this.sentHistoryModal.errorRaw = null
                this.sentHistoryModal.errorTranslation = null
              }
            }
            // Cron health
            if (healthRes.status === 'fulfilled' && healthRes.value?.success) {
              this.sentHistoryModal.cronHealth = healthRes.value?.payload?.pullInbox || null
            }
            // Aktualizacja minutesSinceSent (rośnie z czasem).
            if (this.sentHistoryModal.orderId) {
              const ord = (this.route?.orders || []).find(o => this._orderId(o) === this.sentHistoryModal.orderId)
              const u = this._getSnapshot(ord)?.invoice?.sent?.sentAt?.unix
              if (u) this.sentHistoryModal.minutesSinceSent = Math.floor((Math.floor(Date.now() / 1000) - u) / 60)
            }
            this._diagnoseSentHistoryProblem()
          },
          /** Mapuje status SENT na czytelny dla człowieka tekst. */
          humanizeSentStatus(status) {
            const map = {
              PENDING: 'oczekuje na odpowiedź PUESC',
              CONFIRMED: 'zatwierdzone — gotowe do rozładunku',
              DELIVERED_AWAITING_RECIPIENT: 'po rozładunku, oczekuje na potwierdzenie odbiorcy',
              RECIPIENT_CONFIRMED: 'zamknięte przez odbiorcę',
              OVERDUE: 'spóźnione — odbiorca nie potwierdził',
              BUSINESS_ERROR: 'odrzucone przez PUESC',
              UNKNOWN: 'status nieznany',
            }
            return map[status] || (status ? status : 'brak')
          },
          /** "0 min temu" → "przed chwilą", "1 min temu" → "1 min temu", itp. */
          formatMinutesAgo(mins) {
            if (mins == null) return ''
            if (mins <= 0) return 'przed chwilą'
            if (mins === 1) return '1 minutę temu'
            if (mins < 60) return `${mins} min temu`
            const h = Math.floor(mins / 60)
            return h === 1 ? '1 godzinę temu' : `${h} godz. temu`
          },
          /**
           * Tłumaczy XSD/SAX błąd z NPP na czytelną wiadomość po polsku.
           * Backend zwraca raw "Dokument nie przeszedł weryfikacji technicznej... cvc-...".
           * Wyciągamy konkretne reguły i mapujemy na human-readable.
           */
          _translateNppError(raw) {
            if (!raw) return null
            const s = String(raw)
            // 1) Brak HouseNumber po Street.
            if (/HouseNumber.*expected/i.test(s) || /element ['"].*?City['"]/i.test(s) && /HouseNumber/i.test(s)) {
              return 'W adresie brakuje numeru domu (np. „Targowa 9"). Popraw adres dostawy w zamówieniu.'
            }
            // 2) Niepoprawny format daty (wysłaliśmy dateTime gdzie XSD chce date).
            const dateBad = s.match(/'([^']+)' is not a valid value for ['"]date['"]/)
            if (dateBad) {
              return `Nieprawidłowy format daty (${dateBad[1]}). PUESC oczekuje samej daty (RRRR-MM-DD), bez godziny. Sprawdź zaplanowane daty wyjazdu/zakończenia trasy.`
            }
            // 3) GeoLocator pattern.
            if (/cvc-pattern-valid/i.test(s) && /LocatorNumber/i.test(s)) {
              const valBad = s.match(/Value ['"]([^'"]+)['"]/)
              return `Numer lokalizatora GPS „${valBad ? valBad[1] : ''}" nie pasuje do formatu wymaganego przez PUESC. ` +
                     'Lokalizator musi mieć formę nadaną w SISC (np. Z00ABCDEF12-3) — albo zostaw pole puste w pojeździe, jeśli nie używasz oficjalnego trackera.'
            }
            // 4) Niepoprawny CN.
            if (/CodeCnClassification/i.test(s) || /CN classification/i.test(s)) {
              return 'Nieprawidłowy kod CN paliwa. Sprawdź definicję produktu w zamówieniu.'
            }
            // 5) Brak wymaganego pola.
            const missing = s.match(/element ['"]([^'"]+)['"]\s*\.\s*One of/)
            if (missing) {
              return `Brakuje wymaganego pola w zgłoszeniu (${missing[1]}). Skontaktuj się z opiekunem systemu — to błąd integracji.`
            }
            // Generic fallback — wyciągnij sam komunikat po "cvc-...".
            const cvc = s.match(/cvc-[\w.-]+:\s*(.+)$/)
            return cvc ? cvc[1].trim() : 'Nieoczekiwany błąd walidacji PUESC. Sprawdź dane zamówienia i wyślij ponownie.'
          },
          _diagnoseSentHistoryProblem() {
            const m = this.sentHistoryModal
            const status = m.dbStatus
            const mins = m.minutesSinceSent
            // Status BUSINESS_ERROR — zawsze pokaż retry, nawet gdy mamy już NPP w historii.
            // Bez tego admin nie miał możliwości ponownej wysyłki po odrzuceniu.
            if (status === 'BUSINESS_ERROR') {
              m.problemHint = 'Zgłoszenie zostało odrzucone przez PUESC. Popraw dane i wyślij ponownie — historia poprzednich prób zostanie zachowana.'
              m.canRetry = true
              return
            }
            // Brak komunikatów + nie BUSINESS_ERROR — diagnozujemy.
            if (m.items.length === 0) {
              if (m.cronHealth?.rateLimited) {
                m.problemHint = 'PUESC ma chwilowy limit pobierania. Komunikaty pojawią się tutaj automatycznie po następnym cyklu (do 10 min).'
                m.canRetry = false
              } else if (mins != null && mins >= 10) {
                m.problemHint = `Wysłaliśmy zgłoszenie ${mins} min temu, ale jeszcze nie ma odpowiedzi z PUESC. Możesz spróbować wysłać ponownie — wcześniejsza wysyłka mogła nie dotrzeć.`
                m.canRetry = true
              } else if (mins != null) {
                m.problemHint = `Zgłoszenie wysłane ${mins === 0 ? 'przed chwilą' : `${mins} min temu`}. Czekamy na odpowiedź z PUESC — może to potrwać do 10 min.`
                m.canRetry = false
              } else {
                m.problemHint = 'Brak informacji o czasie wysyłki. Sprawdź status zamówienia.'
                m.canRetry = false
              }
            }
          },
          /** Wyślij SENT_105 ponownie z poziomu modalu historii. Backend resetuje BUSINESS_ERROR. */
          async retrySentFromHistory() {
            const orderId = this.sentHistoryModal.orderId
            if (!orderId) return
            const ordEntry = (this.route?.orders || []).find(o => this._orderId(o) === orderId)
            if (!ordEntry) {
              this.$store.commit('setNotification', {
                show: true, head: 'Błąd retry',
                subheader: 'Nie znaleziono zamówienia w trasie.', success: false,
              })
              return
            }
            this.sentHistoryModal.retrying = true
            // Tymczasowo wyczyść invoice.sent, żeby sendSentForOrder go nie skipnął.
            // Backend traktuje to jako retry (znajduje istniejący Sent po documentId).
            try {
              await db.collection('FuelOrders').doc(orderId).update({ 'invoice.sent': null })
              await this.downloadOrdersData()
              const refreshed = (this.route?.orders || []).find(o => this._orderId(o) === orderId)
              const r = await this.sendSentForOrder(refreshed || ordEntry, { silent: false, skipRefresh: false })
              if (r?.success) {
                // Po retry pokaż świeżą historię.
                this.closeSentHistory()
                setTimeout(() => this.openSentHistory(refreshed || ordEntry), 200)
              }
            } catch (err) {
              console.error('[SENT] retrySentFromHistory error:', err)
              this.$store.commit('setNotification', {
                show: true, head: 'Błąd retry SENT',
                subheader: err?.message || 'Nie udało się ponowić wysyłki.', success: false,
              })
            } finally {
              this.sentHistoryModal.retrying = false
            }
          },
          closeSentHistory() {
            if (this.sentHistoryModal.refreshTimer) {
              clearInterval(this.sentHistoryModal.refreshTimer)
            }
            this.sentHistoryModal = {
              show: false, orderId: '', loading: false, error: null, items: [], downloadingIdx: null,
              dbStatus: null, minutesSinceSent: null, cronHealth: null,
              problemHint: null, canRetry: false, retrying: false,
              errorTranslation: null, errorRaw: null,
              refreshTimer: null,
            }
          },
          /** Pobierz surowy plik (XML/PDF) komunikatu PUESC i wymuś download w przeglądarce. */
          async downloadSentMessage(item) {
            const documentId = item?.documentId || this.sentHistoryModal.orderId
            if (!documentId || item?.idx == null) return
            this.sentHistoryModal.downloadingIdx = item.idx
            try {
              const blob = await this._sentApiClient().getMessageRaw(documentId, item.idx)
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              const safeName = item.filename || `${item.messageType || 'message'}_${item.idx}`
              a.download = safeName
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
              setTimeout(() => URL.revokeObjectURL(url), 1000)
            } catch (err) {
              console.error('[SENT] getMessageRaw error:', err)
              this.$store.commit('setNotification', {
                show: true, head: 'Błąd pobierania',
                subheader: err?.message || 'Nie udało się pobrać pliku.', success: false
              })
            } finally {
              this.sentHistoryModal.downloadingIdx = null
            }
          },
          formatSentReceivedAt(iso) {
            if (!iso) return '—'
            const m = moment(iso)
            return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '—'
          },
          formatSentMime(mime) {
            if (!mime) return '—'
            if (mime.includes('xml')) return 'XML'
            if (mime.includes('pdf')) return 'PDF'
            return mime
          },
          /**
           * Wysyłka SENT_105 dla pojedynczego zamówienia + zapis w Firestore.
           * Zwraca { success, errorMessage?, rejected? } — bulk korzysta do liczników.
           * opts.silent — pomija toasty (bulk agreguje).
           * opts.skipRefresh — pomija per-order downloadOrdersData/refresh (bulk wywoła raz).
           */
          async sendSentForOrder(ordEntry, opts = {}) {
            const orderId = this._orderId(ordEntry)
            if (!orderId) return { success: false, errorMessage: 'Brak ID zamówienia.' }
            const snap = this._getSnapshot(ordEntry)
            if (snap?.invoice?.sent?.documentId) {
              if (!opts.silent) {
                this.$store.commit('setNotification', {
                  show: true, head: 'SENT już wysłany',
                  subheader: 'To zamówienie ma już zarejestrowany SENT.', success: false
                })
              }
              return { success: false, errorMessage: 'SENT już wysłany.' }
            }
            this.sent.loadingByOrderId = { ...this.sent.loadingByOrderId, [orderId]: true }
            let result = { success: false, errorMessage: 'Nieznany błąd.' }
            try {
              const api = this._sentApiClient()
              // buildSent105FromOrder rzuca błędem gdy adres bez numeru domu.
              const payload = buildSent105FromOrder(snap, this.route)
              const res = await api.sendSent105(payload)
              if (res?.success) {
                await db.collection('FuelOrders').doc(orderId).update({
                  'invoice.sent': {
                    documentId: orderId,
                    sentAt: { unix: moment().unix(), asText: moment().format('YYYY-MM-DD HH:mm:ss') },
                    profile: 'demo',
                  },
                })
                if (!opts.silent) {
                  this.$store.commit('setNotification', {
                    show: true, head: 'SENT wysłany',
                    subheader: `Zgłoszenie ${orderId} wysłane do PUESC. Status: w trakcie weryfikacji.`,
                    success: true
                  })
                }
                result = { success: true }
              } else {
                const errCode = res?.error?.code || ''
                const errMsg = res?.message || res?.error?.codeMessage || 'Nieznany błąd'
                if (errCode === 'SENT_105_REJECTED') {
                  await db.collection('FuelOrders').doc(orderId).update({
                    'invoice.sent': {
                      documentId: orderId,
                      sentAt: { unix: moment().unix(), asText: moment().format('YYYY-MM-DD HH:mm:ss') },
                      profile: 'demo',
                    },
                  })
                }
                if (!opts.silent) {
                  this.$store.commit('setNotification', {
                    show: true, head: 'Błąd wysyłki SENT',
                    subheader: errMsg, success: false
                  })
                }
                result = { success: false, errorMessage: errMsg, rejected: errCode === 'SENT_105_REJECTED' }
              }
            } catch (err) {
              console.error('[SENT] sendSentForOrder error:', err)
              const msg = err?.message || 'Nieznany błąd sieci.'
              if (!opts.silent) {
                this.$store.commit('setNotification', {
                  show: true, head: 'Błąd wysyłki SENT',
                  subheader: msg, success: false
                })
              }
              result = { success: false, errorMessage: msg }
            } finally {
              this.sent.loadingByOrderId = { ...this.sent.loadingByOrderId, [orderId]: false }
              if (!opts.skipRefresh) {
                await this.downloadOrdersData()
                await this.refreshAllSentStatuses()
              }
            }
            return result
          },
          /** Bulk: dla wszystkich zamówień bez invoice.sent.documentId. */
          async generateSentForRoute() {
            if (this.sent.bulkLoading) return
            const candidates = (this.route?.orders || []).filter(o => {
              const snap = this._getSnapshot(o)
              return !snap?.invoice?.sent?.documentId
            })
            if (candidates.length === 0) {
              this.$store.commit('setNotification', {
                show: true, head: 'Brak nowych zgłoszeń',
                subheader: 'Wszystkie zamówienia w trasie mają już SENT.', success: false
              })
              return
            }
            this.sent.bulkLoading = true
            let okCount = 0, errCount = 0
            const errorMessages = []
            try {
              for (const ord of candidates) {
                // silent + skipRefresh — bulk agreguje notyfikacje i refreshuje raz na końcu.
                const r = await this.sendSentForOrder(ord, { silent: true, skipRefresh: true })
                if (r?.success) {
                  okCount++
                } else {
                  errCount++
                  if (r?.errorMessage) errorMessages.push(r.errorMessage)
                }
              }
              const subheader = errCount > 0
                ? (errorMessages.slice(0, 2).join(' · ') + (errorMessages.length > 2 ? '…' : ''))
                : 'Statusy w trakcie weryfikacji.'
              this.$store.commit('setNotification', {
                show: true,
                head: `Wysłano ${okCount} z ${candidates.length}${errCount > 0 ? ` (${errCount} odrzucone)` : ''}`,
                subheader,
                success: errCount === 0
              })
            } finally {
              // Jeden refresh dla całej trasy, niezależnie od liczby zamówień.
              await this.downloadOrdersData()
              await this.refreshAllSentStatuses()
              this.sent.bulkLoading = false
            }
          },
          /** Modal-guard: gdy zamówienie wysłane do PUESC, ostrzeżenie przed "niedostarczone". */
          requestMarkUndelivered(ord) {
            if (this._orderHasSent(ord)) {
              this.sentUndeliveredConfirm = { show: true, ord }
            } else {
              // Bez SENT — od razu stary flow.
              this.undeliveredConfirm.show = true
              this.undeliveredConfirm.ord = ord
            }
          },
          confirmSentUndelivered() {
            const ord = this.sentUndeliveredConfirm.ord
            this.sentUndeliveredConfirm = { show: false, ord: null }
            // Po potwierdzeniu otwórz stary modal undeliveredConfirm
            this.undeliveredConfirm.show = true
            this.undeliveredConfirm.ord = ord
          },
          /** Modal-guard: usunięcie z trasy zamówienia z SENT. */
          requestRemoveFromEdit(index) {
            const ord = this.editData.orders[index]
            const orderId = this._orderId(ord)
            const snap = this._getSnapshot(ord)
            if (snap?.invoice?.sent?.documentId) {
              this.sentRemoveFromRouteConfirm = { show: true, indexToRemove: index, orderId }
            } else {
              this.removeOrderFromEdit(index)
            }
          },
          confirmSentRemoveFromRoute() {
            const idx = this.sentRemoveFromRouteConfirm.indexToRemove
            this.sentRemoveFromRouteConfirm = { show: false, indexToRemove: -1, orderId: '' }
            this.removeOrderFromEdit(idx)
          },
          openMapPreview(deliveryAddress) {
            this.mapPreviewModal.deliveryAddress = deliveryAddress || null
            this.mapPreviewModal.show = true
          },
          closeMapPreview() {
            this.mapPreviewModal.show = false
            // Zachowaj deliveryAddress żeby nie było migania podczas zamykania.
            setTimeout(() => { this.mapPreviewModal.deliveryAddress = null }, 200)
          },
          routeStatusLabel(code) {
            switch (code) {
              case 1: return 'Zaplanowana'
              case 2: return 'W realizacji'
              case 3: return 'Zakończona'
              case 4: return 'Anulowana'
              default: return 'Nieznany'
            }
          },
          routeStatusColor(code) {
            switch (code) {
              case 1: return 'text-blue-300'
              case 2: return 'text-purple-300'
              case 3: return 'text-green-300'
              case 4: return 'text-red-300'
              default: return 'text-slate-400'
            }
          },
          routeStatusChipClass(code) {
            switch (code) {
              case 1: return 'statusChipBlue'
              case 2: return 'statusChipYellow'
              case 3: return 'statusChipGreen'
              case 4: return 'statusChipRed'
              default: return ''
            }
          },
          openUnloadModal(ord) {
            const snap = ord.freshSnapshot || ord.snapshotAtAddingTime
            const ordId = ord.id || (ord.ref && ord.ref.id) || null
            this.unloadModal.order = snap
            this.unloadModal.orderId = ordId
            this.unloadModal.quantityInput = String(snap?.orderedFuel?.quantityLiters ?? '')
            this.unloadModal.saving = false
            this.unloadModal.show = true
          },
          sanitizeUnloadInput() {
            let val = this.unloadModal.quantityInput
            // Zamiana przecinka na kropkę
            val = val.replace(/,/g, '.')
            // Usunięcie wszystkiego poza cyframi i kropką
            val = val.replace(/[^0-9.]/g, '')
            // Tylko jedna kropka
            const parts = val.split('.')
            if (parts.length > 2) {
              val = parts[0] + '.' + parts.slice(1).join('')
            }
            // Max 2 miejsca po przecinku
            if (parts.length === 2 && parts[1].length > 2) {
              val = parts[0] + '.' + parts[1].substring(0, 2)
            }
            this.unloadModal.quantityInput = val
          },
          async confirmUnload() {
            const qty = parseFloat(this.unloadModal.quantityInput)
            if (!Number.isFinite(qty) || qty <= 0) {
              this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: 'Podaj prawidłową ilość rozładowanego paliwa.', success: false })
              return
            }
            const ordId = this.unloadModal.orderId
            if (!ordId) return
            this.unloadModal.saving = true
            try {
              const snap = this.unloadModal.order
              await db.collection('FuelOrders').doc(ordId).update({
                status: 5,
                delivered: true,
                unloadedFuel: {
                  fuelId: snap.orderedFuel.fuelId,
                  fuelName: snap.orderedFuel.fuelName,
                  quantityLiters: qty
                },
                finalDeliveryDate: {
                  date: new Date(),
                  unix: moment().unix(),
                  asText: moment().format('YYYY-MM-DD')
                }
              })
              // Jeśli zamówienie ma zarejestrowany SENT, oznacz dostawę w urzad-api.
              // Startuje cron przypomnień u nas (kierowca rozładował → klient ma N dni
              // roboczych na potwierdzenie odbioru).
              if (snap?.invoice?.sent?.documentId) {
                try {
                  const api = this._sentApiClient()
                  await api.markDelivered(snap.invoice.sent.documentId)
                } catch (sentErr) {
                  console.warn('[SENT] markDelivered failed:', sentErr?.message)
                  // Nie blokujemy rozładunku — operator może spróbować ponownie ręcznie.
                }
              }

              this.unloadModal.show = false
              this.$store.commit('setNotification', { show: true, head: 'Zamówienie dostarczone!', subheader: `Rozładowano ${qty} L.`, success: true })
              await this.downloadOrdersData()
              await this.refreshAllSentStatuses()
              // Wymuś reaktywność również w editData.orders jeżeli jesteśmy w edit mode
              if (this.editMode && this.editData && Array.isArray(this.editData.orders)) {
                this.editData.orders = this.editData.orders.map(o => {
                  const id = o.id || (o.ref && o.ref.id)
                  if (id !== ordId) return o
                  const fresh = (this.route.orders || []).find(r => (r.id || (r.ref && r.ref.id)) === ordId)
                  return { ...o, freshSnapshot: fresh ? fresh.freshSnapshot : o.freshSnapshot }
                })
              }
            } catch (e) {
              console.error(e)
              this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: 'Nie udało się oznaczyć zamówienia jako dostarczone.', success: false })
            } finally {
              this.unloadModal.saving = false
            }
          },
          generateInvoiceForOrder(ord) {
            const ordId = ord.id || (ord.ref && ord.ref.id) || null
            if (!ordId) return
            const snap = ord.freshSnapshot
            if (!snap || snap.status !== 5 || !snap.unloadedFuel) return

            const clientName = snap.client?.invoiceAddress?.name || 'klient'
            this.invoiceConfirm = {
              show: true,
              title: 'Wystaw fakturę',
              message: `Czy na pewno chcesz wystawić fakturę za dostawę ${snap.unloadedFuel.quantityLiters} L dla ${clientName}?`,
              confirmText: 'Wystaw fakturę',
              onConfirm: () => this._doGenerateInvoice(ordId, snap)
            }
          },
          async _doGenerateInvoice(ordId, snap) {
            this.invoiceGenerating = ordId
            try {
              const res = await axios.post(`${this.$store.state.apiLink}/generateFuelDeliveryInvoice`, {
                orders: [{ orderId: ordId, userId: snap.client?.systemUserId || null }]
              })
              if (res.data.success && res.data.data?.results?.length > 0) {
                const r = res.data.data.results[0]
                if (r.success) {
                  this.$store.commit('setNotification', { show: true, head: 'Faktura wystawiona!', subheader: `${r.invoiceName} — wartość brutto: ${r.grossValue} PLN`, success: true })
                } else {
                  this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: r.log || 'Nie udało się wystawić faktury.', success: false })
                }
                await this.downloadOrdersData()
              } else {
                this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: res.data.log || 'Nie udało się wystawić faktury.', success: false })
              }
            } catch (e) {
              console.error(e)
              this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: 'Nie udało się wystawić faktury.', success: false })
            } finally {
              this.invoiceGenerating = null
            }
          },
          generateAllInvoices() {
            const orders = (this.route?.orders || [])
              .filter(o => {
                if (o.undelivered) return false
                const snap = o.freshSnapshot
                if (!snap || snap.status !== 5 || !snap.unloadedFuel) return false
                if (snap.invoice?.invoiced) return false
                return true
              })
              .map(o => ({
                orderId: o.id || (o.ref && o.ref.id),
                userId: o.freshSnapshot?.client?.systemUserId || null
              }))
              .filter(o => o.orderId)

            if (orders.length === 0) return

            this.invoiceConfirm = {
              show: true,
              title: 'Generuj faktury',
              message: `Czy na pewno chcesz wygenerować faktury dla ${orders.length} zamówień?`,
              confirmText: `Generuj ${orders.length} faktur`,
              onConfirm: () => this._doGenerateAllInvoices(orders)
            }
          },
          async _doGenerateAllInvoices(orders) {
            this.generatingAllInvoices = true
            try {
              const res = await axios.post(`${this.$store.state.apiLink}/generateFuelDeliveryInvoice`, { orders })
              if (res.data.success) {
                const results = res.data.data?.results || []
                const ok = results.filter(r => r.success).length
                const fail = results.filter(r => !r.success).length
                this.$store.commit('setNotification', {
                  show: true,
                  head: `Wygenerowano ${ok} faktur`,
                  subheader: fail > 0 ? `${fail} zamówień nie udało się zafakturować.` : 'Wszystkie faktury wystawione pomyślnie.',
                  success: fail === 0
                })
                await this.downloadOrdersData()
              } else {
                this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: res.data.log || 'Nie udało się wygenerować faktur.', success: false })
              }
            } catch (e) {
              console.error(e)
              this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: 'Nie udało się wygenerować faktur.', success: false })
            } finally {
              this.generatingAllInvoices = false
            }
          },
          async markOrderUndelivered(ord) {
            const ordId = ord.id || (ord.ref && ord.ref.id) || null
            if (!ordId) return
            try {
              // 1. W FuelOrders: wracaj do puli
              await db.collection('FuelOrders').doc(ordId).update({
                status: 2,
                isInRoute: false,
                routeRef: null,
                delivered: false,
                unloadedFuel: null,
                deliveryAttempted: true
              })

              // 2. W Route doc: oznacz zamówienie jako niedostarczone (zostaje w liście trasy)
              const routeOrders = this.route.orders || []
              const ordersForSave = routeOrders.map((o, idx) => {
                const id = o.id || (o.ref && o.ref.id)
                const cleaned = { ...o }
                delete cleaned.freshSnapshot
                if (id) cleaned.ref = db.collection('FuelOrders').doc(id)
                cleaned.sortIndex = idx
                if (id === ordId) cleaned.undelivered = true
                return cleaned
              })
              await db.collection('Routes').doc(this.route.id).update({ orders: ordersForSave })

              // 3. Pobierz świeże dane z Firestore
              await this.downloadOrdersData()

              // 4. Wymuś reaktywność — podmień cały array na nowe obiekty z flagą undelivered
              if (this.route && this.route.orders) {
                this.route.orders = this.route.orders.map(o => {
                  const id = o.id || (o.ref && o.ref.id)
                  return id === ordId ? { ...o, undelivered: true } : o
                })
              }
              // 5. Jeżeli jesteśmy w trybie edycji, zsynchronizuj editData.orders (displayOrders)
              if (this.editMode && this.editData && Array.isArray(this.editData.orders)) {
                this.editData.orders = this.editData.orders.map(o => {
                  const id = o.id || (o.ref && o.ref.id)
                  if (id !== ordId) return o
                  // Skopiuj też świeży freshSnapshot z route.orders (ma już status 2)
                  const fresh = (this.route.orders || []).find(r => (r.id || (r.ref && r.ref.id)) === ordId)
                  return { ...o, undelivered: true, freshSnapshot: fresh ? fresh.freshSnapshot : o.freshSnapshot }
                })
              }

              this.$store.commit('setNotification', { show: true, head: 'Zamówienie oznaczone jako niedostarczone', subheader: 'Zamówienie wróciło do puli zamówień do realizacji.', success: true })
            } catch (e) {
              console.error(e)
              this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: 'Nie udało się oznaczyć zamówienia.', success: false })
            }
          },
          fuelOrderStatusLabel(code) {
            switch (code) {
              case 1: return 'Oczekuje'
              case 2: return 'Do realizacji'
              case 3: return 'Oczekuje na wyjazd'
              case 4: return 'W trakcie realizacji'
              case 5: return 'Dostarczone'
              case 6: return 'Anulowane'
              default: return 'Nieznany'
            }
          },

          /* ── EDIT MODE ── */
          async enableEditMode() {
            this.editData.name = this.route.name || ''
            this.editData.note = this.route.note || ''
            this.editData.status = this.route.status || 1
            this.editData.orders = JSON.parse(JSON.stringify(this.route.orders || []))
            this.editData.removedOrders = []
            this.editData.originalOrderIds = (this.route.orders || []).map(o => o.id).filter(Boolean)

            const s = this.route.plannedStart?.unix || this.route.plannedStart?.date?.seconds || this.route.plannedStart?.date?._seconds
            const e = this.route.plannedEnd?.unix || this.route.plannedEnd?.date?.seconds || this.route.plannedEnd?.date?._seconds
            this.editDate = s && e ? [moment.unix(s).toDate(), moment.unix(e).toDate()] : null

            await Promise.all([this.getDrivers(), this.getRegions(), this.getVehicles(), this.getTrails()])

            this.editSelectedDriver = this.drivers.all.find(d => d.id === this.route.driver?.id) || null
            this.editSelectedRegion = this.regions.all.find(r => r.id === this.route.mainRegion?.id) || null
            this.editSelectedVehicles = this.vehicles.find(v => v.id === this.route.vehicle?.id) || null
            this.editSelectedTrails = this.trails.find(t => t.id === this.route.trailer?.id) || null

            this.editMode = true
          },

          cancelEditMode() {
            this.editMode = false
            this.editData = { name: '', note: '', orders: [], removedOrders: [], originalOrderIds: [] }
            this.editDate = null
            this.editSelectedDriver = null
            this.editSelectedRegion = null
            this.editSelectedVehicles = null
            this.editSelectedTrails = null
          },

          async saveRouteEdits() {
            if (!this.route || !this.route.id) return
            this.saving = true

            try {
              // IDs zamówień które zostały reaktywowane z "niedostarczone"
              const reactivatedIds = this.editData.orders
                .filter(o => o._reactivated === true)
                .map(o => o.id || (o.ref && o.ref.id))
                .filter(Boolean)

              const ordersForDb = this.editData.orders.map((o, idx) => {
                const restored = { ...o }
                // nie zapisujemy freshSnapshot ani wewnętrznego flaga reaktywacji
                delete restored.freshSnapshot
                delete restored._reactivated
                restored.sortIndex = idx
                // reaktywowane zamówienia tracą flagę undelivered w Route doc
                if (o._reactivated === true) restored.undelivered = false
                if (o.id) {
                  restored.ref = db.collection('FuelOrders').doc(o.id)
                }
                return restored
              })

              // Build updated dates
              let updatedPlannedStart = this.route.plannedStart
              let updatedPlannedEnd = this.route.plannedEnd
              if (this.editDate && this.editDate[0] && this.editDate[1]) {
                updatedPlannedStart = { date: moment(this.editDate[0]).toDate(), unix: moment(this.editDate[0]).unix() }
                updatedPlannedEnd = { date: moment(this.editDate[1]).toDate(), unix: moment(this.editDate[1]).unix() }
              }

              // Build updated driver
              let updatedDriver = this.route.driver
              if (this.editSelectedDriver) {
                const d = this.editSelectedDriver
                updatedDriver = { id: d.id, name: d.name + ' ' + d.surname, phone: d.contact?.phone || '', email: d.contact?.email || '', ref: db.collection('WorkersData').doc(d.id) }
              }

              // Build updated region
              let updatedMainRegion = this.route.mainRegion
              if (this.editSelectedRegion) {
                updatedMainRegion = { id: this.editSelectedRegion.id, name: this.editSelectedRegion.name }
              }

              // Build updated vehicle
              let updatedVehicle = this.route.vehicle
              if (this.editSelectedVehicles) {
                const v = this.editSelectedVehicles
                updatedVehicle = { id: v.id, brand: v.brand, model: v.model, licensePlate: v.licensePlate, chambers: v.chambers, tankLitreCapacity: v.tankLitreCapacity, roadPermissionNumber: v.roadPermissionNumber || '', gpsTrackerNumber: v.gpsTrackerNumber || '', containsProduct: { id: 30, name: 'Shell FuelSave Diesel CN 27102011' }, ref: db.collection('Fleet').doc(v.id) }
              }

              // Build updated trailer
              let updatedTrailer = this.editSelectedTrails !== undefined
                ? (this.editSelectedTrails === null ? null : (() => {
                    const t = this.editSelectedTrails
                    return { id: t.id, brand: t.brand, model: t.model, licensePlate: t.licensePlate, chambers: t.chambers, tankLitreCapacity: t.tankLitreCapacity, containsProduct: { id: 30, name: 'Shell FuelSave Diesel CN 27102011' }, ref: db.collection('Fleet').doc(t.id) }
                  })())
                : this.route.trailer

              const updatedTotalLitre = Number(updatedVehicle?.tankLitreCapacity || 0) + Number(updatedTrailer?.tankLitreCapacity || 0)

              await db.collection('Routes').doc(this.route.id).update({
                name: this.editData.name,
                note: this.editData.note,
                orders: ordersForDb,
                plannedStart: updatedPlannedStart,
                plannedEnd: updatedPlannedEnd,
                driver: updatedDriver,
                mainRegion: updatedMainRegion,
                vehicle: updatedVehicle,
                trailer: updatedTrailer,
                totalLitreCapacity: updatedTotalLitre,
              })

              // Build map of order statuses from freshSnapshots
              const orderStatusMap = {}
              for (const ord of (this.route.orders || [])) {
                const id = ord.id || (ord.ref && ord.ref.id)
                if (id && ord.freshSnapshot) orderStatusMap[id] = ord.freshSnapshot.status
              }

              // Update isInRoute for added/removed orders
              const finalIds = this.editData.orders.map(o => o.id).filter(Boolean)
              const addedIds = finalIds.filter(id => !this.editData.originalOrderIds.includes(id))
              const removedIds = this.editData.originalOrderIds.filter(id => !finalIds.includes(id))
              // Determine fuel order status based on current route status
              const orderStatusForRoute = this.route.status === 2 ? 4 : 3

              if (addedIds.length > 0 || removedIds.length > 0 || reactivatedIds.length > 0) {
                const isInRouteBatch = db.batch()
                const routeRef = db.collection('Routes').doc(this.route.id)
                addedIds.forEach(id => isInRouteBatch.update(db.collection('FuelOrders').doc(id), { status: orderStatusForRoute, isInRoute: true, routeRef, deliveryAttempted: false }))
                // Reaktywowane niedostarczone: wracają do trasy ze statusem 4/3
                reactivatedIds.forEach(id => isInRouteBatch.update(db.collection('FuelOrders').doc(id), { status: orderStatusForRoute, isInRoute: true, routeRef, deliveryAttempted: false }))
                removedIds.forEach(id => {
                  if ((orderStatusMap[id] || 0) < 5) isInRouteBatch.update(db.collection('FuelOrders').doc(id), { status: 2, isInRoute: false, routeRef: null })
                })
                await isInRouteBatch.commit()
              }

              // Handle route status change and propagate to orders

              const newStatus = this.editData.status
              const oldStatus = this.route.status
              if (newStatus !== oldStatus) {
                const statusBatch = db.batch()
                const allOrderIds = this.editData.orders.map(o => o.id).filter(Boolean)

                if (newStatus === 3 && !this.canFinishRoute) {
                  this.$store.commit('setNotification', { show: true, head: 'Nie można zakończyć trasy', subheader: this.finishBlockReason, success: false })
                  this.saving = false
                  return
                }
                if (newStatus === 4 && this.hasDeliveredOrders) {
                  this.$store.commit('setNotification', { show: true, head: 'Nie można anulować trasy', subheader: 'Trasa ma dostarczone zamówienia.', success: false })
                  this.saving = false
                  return
                }

                if (newStatus === 2) {
                  // Trasa ruszyła → tylko zamówienia ze status < 5 do W trakcie (4)
                  allOrderIds.forEach(id => {
                    if ((orderStatusMap[id] || 0) < 5) statusBatch.update(db.collection('FuelOrders').doc(id), { status: 4 })
                  })
                } else if (newStatus === 3) {
                  // Trasa zakończona — zamówienia już mają status 5 (dostarczone indywidualnie), nie zmieniamy
                } else if (newStatus === 4) {
                  // Trasa anulowana → zamówienia ze status < 5 wracają do "Do realizacji" (2)
                  allOrderIds.forEach(id => {
                    if ((orderStatusMap[id] || 0) < 5) statusBatch.update(db.collection('FuelOrders').doc(id), { status: 2, isInRoute: false, routeRef: null })
                  })
                } else if (newStatus === 1 && oldStatus > 1) {
                  // Cofnięcie do Zaplanowana → tylko zamówienia ze status === 4 cofaj do 3
                  allOrderIds.forEach(id => {
                    if (orderStatusMap[id] === 4) statusBatch.update(db.collection('FuelOrders').doc(id), { status: 3 })
                  })
                }

                // Update route status + archive
                const routeStatusUpdate = { status: newStatus }
                if (newStatus === 3 || newStatus === 4) {
                  routeStatusUpdate.archive = true
                } else {
                  routeStatusUpdate.archive = false
                }
                statusBatch.update(db.collection('Routes').doc(this.route.id), routeStatusUpdate)
                await statusBatch.commit()

                this.route.status = newStatus
                this.route.archive = routeStatusUpdate.archive
              }

              this.route.name = this.editData.name
              this.route.note = this.editData.note
              this.route.orders = ordersForDb
              this.route.plannedStart = updatedPlannedStart
              this.route.plannedEnd = updatedPlannedEnd
              this.route.driver = updatedDriver
              this.route.mainRegion = updatedMainRegion
              this.route.vehicle = updatedVehicle
              this.route.trailer = updatedTrailer
              this.route.totalLitreCapacity = updatedTotalLitre
              this.editData.originalOrderIds = finalIds

              this.$store.commit('setNotification', {
                show: true,
                head: 'Trasa zaktualizowana!',
                subheader: 'Zmiany zostały zapisane.',
                success: true
              })

              this.editMode = false
              this.downloadOrdersData()
            } catch (e) {
              console.error(e)
              this.$store.commit('setNotification', {
                show: true,
                head: 'Błąd zapisu',
                subheader: 'Nie udało się zapisać zmian.',
                success: false
              })
            } finally {
              this.saving = false
            }
          },

          /* ── ORDER REORDERING ── */
          moveOrder(index, direction) {
            const newIndex = index + direction
            if (newIndex < 0 || newIndex >= this.editData.orders.length) return
            const arr = this.editData.orders
            const tmp = arr[index]
            arr.splice(index, 1)
            arr.splice(newIndex, 0, tmp)
          },

          onDragStart(index, event) {
            this.dragIndex = index
            event.dataTransfer.effectAllowed = 'move'
          },

          onDragOver(index, event) {
            this.dragOverIndex = index
            event.dataTransfer.dropEffect = 'move'
          },

          onDragLeave(event) {
            this.dragOverIndex = null
          },

          onDrop(index, event) {
            this.dragOverIndex = null
            if (this.dragIndex === null || this.dragIndex === index) return
            const arr = this.editData.orders
            const item = arr.splice(this.dragIndex, 1)[0]
            arr.splice(index, 0, item)
            this.dragIndex = null
          },

          /* ── PDF: WAYBILLS ── */
          downloadWaybillsPdfForAllOrders() {
            try {
              if (!this.route || !Array.isArray(this.route.orders) || this.route.orders.length === 0) return;
              const cc = this._getChamberConfig(this.route)
              if (cc.mode === 'none') {
                this.$store.commit('setNotification', {
                  show: true,
                  head: 'Brak komór w zestawie transportowym',
                  subheader: 'Uzupełnij komory pojazdu lub naczepy, aby wygenerować listy przewozowe.',
                  success: false
                })
                return
              }
              const pages = [];
              for (let i = 0; i < this.route.orders.length; i++) {
                const ord = this.route.orders[i];
                const data = this._buildWaybillDataFromRouteAndOrder(this.route, ord);
                const ddOne = this._buildWaybillDd(data);
                const oneContent = Array.isArray(ddOne.content) ? ddOne.content : [];
                for (let k = 0; k < oneContent.length; k++) {
                  pages.push(oneContent[k]);
                }
                if (i !== this.route.orders.length - 1) {
                  pages.push({ text: "", pageBreak: "after" });
                }
              }
              const ddBase = this._buildWaybillDd(
                this._buildWaybillDataFromRouteAndOrder(this.route, this.route.orders[0])
              );
              const ddMerged = {
                pageSize: ddBase.pageSize,
                pageMargins: ddBase.pageMargins,
                styles: ddBase.styles,
                defaultStyle: ddBase.defaultStyle,
                content: pages,
              };
              const fileName = `Listy_przewozowe_${this.route?.id || this.$route.query.id || "trasa"}.pdf`;
              pdfMake.createPdf(ddMerged).download(fileName);
            } catch (e) {
              console.error("Waybills (ALL) PDF error:", e);
            }
          },
            _getSnapshot(ord) {
              return ord?.freshSnapshot != null ? ord.freshSnapshot : ord?.snapshotAtAddingTime;
            },
            _clean(s) {
              return String(s ?? "").replace(/\s+/g, " ").trim();
            },
            _formatDestinationFromDeliveryAddress(deliveryAddress) {
              if (!deliveryAddress) return "";
              const a = this._clean(deliveryAddress.address);
              const pc = this._clean(deliveryAddress.postCode);
              const city = this._clean(deliveryAddress.city);
              const pcCity = this._clean(`${pc} ${city}`);
              return [a, pcCity].filter(Boolean).join("\n");
            },
            _formatSenderBlock(sender) {
              const lines = [];
              if (sender?.name) lines.push(sender.name);
              if (sender?.nip) lines.push(`NIP: ${sender.nip}`);
              else if (sender?.bdo) lines.push(sender.bdo);
              if (sender?.street) lines.push(sender.street);
              const pcCity = this._clean(`${sender?.postalCode || ""} ${sender?.city || ""}`);
              if (pcCity) lines.push(pcCity);
              return lines.join("\n");
            },
            _getProductMeta(fuelName) {
              const key = this._clean(fuelName);
              const PRODUCT_MAP = {
                "Shell FuelSave Diesel CN 27102011": {
                  symbol: "ON", mag: "M09", group: "ON", index: "11797", gtu: "GTU_2", unit: "L", vat: "23 %",
                  properName: "UN 1202, Olej napędowy 3, III, (D,E)",
                },
              };
              if (PRODUCT_MAP[key]) return PRODUCT_MAP[key];
              const keys = Object.keys(PRODUCT_MAP);
              for (let i = 0; i < keys.length; i++) {
                if (key.startsWith(keys[i])) return PRODUCT_MAP[keys[i]];
              }
              return { symbol: "", mag: "", group: "", index: "", gtu: "", unit: "L", vat: "23 %", properName: "" };
            },

            /**
             * Determine chamber layout configuration based on vehicle/trailer capacities.
             * Returns { mode, vehicleChambers, trailerChambers, vehicleLabel, trailerLabel }
             */
            _getChamberConfig(route) {
              const vehicle = route?.vehicle || null
              const trailer = route?.trailer || null
              const vCap = Number(vehicle?.tankLitreCapacity ?? 0)
              const tCap = Number(trailer?.tankLitreCapacity ?? 0)
              const vChambers = Array.isArray(vehicle?.chambers) ? vehicle.chambers : []
              const tChambers = Array.isArray(trailer?.chambers) ? trailer.chambers : []

              if (vCap > 0 && tCap > 0) {
                // Solo + Przyczepa
                return {
                  mode: 'solo_przyczepa',
                  vehicleChambers: vChambers,
                  trailerChambers: tChambers,
                  vehicleLabel: 'Nr komory autocysterny:',
                  trailerLabel: 'Nr komory przyczepy:',
                }
              } else if (vCap > 0 && tCap === 0) {
                // Solo — only vehicle chambers
                return {
                  mode: 'solo',
                  vehicleChambers: vChambers,
                  trailerChambers: [],
                  vehicleLabel: 'Nr komory autocysterny:',
                  trailerLabel: '',
                }
              } else if (vCap === 0 && tCap > 0) {
                // Cysterna — only trailer chambers
                return {
                  mode: 'cysterna',
                  vehicleChambers: [],
                  trailerChambers: tChambers,
                  vehicleLabel: '',
                  trailerLabel: 'Nr komory naczepy:',
                }
              }
              // No chambers at all
              return {
                mode: 'none',
                vehicleChambers: [],
                trailerChambers: [],
                vehicleLabel: '',
                trailerLabel: '',
              }
            },

            /**
             * Given a list of chambers (sorted) and a target litre qty,
             * mark X from left to right until cumulative capacity >= qty.
             */
            _markChambers(chambers, qtyLiters) {
              const marks = []
              let cumulative = 0
              const target = Number(qtyLiters) || 0
              for (let i = 0; i < chambers.length; i++) {
                const cap = Number(chambers[i].capacity || 0)
                if (cumulative < target) {
                  marks.push('X')
                  cumulative += cap
                } else {
                  marks.push('')
                }
              }
              return marks
            },

            _buildWaybillDataFromRouteAndOrder(route, ord) {
              const snap = this._getSnapshot(ord) || {};
              const orderId = this._clean(snap?.id || ord?.id || "");
              const isLauron = !!snap?.invoice?.isLauronInvoice;
              const SENDER_WB = { name: "WB TANK SP. Z O.O.", bdo: "BDO000386131", street: "UDORPIE, UL. CHEŁMIŃSKA 3", postalCode: "77-100", city: "BYTÓW", nip: "" };
              const SENDER_LAURON = { name: "LAURON SP. Z O.O.", nip: "8421772106", street: "PŁOTOWO 3A", postalCode: "77-131", city: "REKOWO", bdo: "" };
              const sender = isLauron ? SENDER_LAURON : SENDER_WB;
              const carrier = sender;
              const plannedStartUnix = route?.plannedStart?.unix || route?.plannedStart?.date?.seconds || route?.plannedStart?.date?._seconds;
              const dateStr = plannedStartUnix ? moment.unix(plannedStartUnix).format("DD.MM.YYYY") : moment().format("DD.MM.YYYY");
              const receiverName = snap?.client?.invoiceAddress?.name || snap?.companyName || "";
              const destination = this._formatDestinationFromDeliveryAddress(snap?.deliveryAddress);
              const fuelName = snap?.orderedFuel?.fuelName || "";
              const qtyLiters = snap?.orderedFuel?.quantityLiters != null ? String(snap.orderedFuel.quantityLiters) : "";
              const pm = this._getProductMeta(fuelName);
              const vehicleNo = route?.vehicle?.licensePlate || "";
              const driverName = route?.driver?.name || "";

              // Chamber configuration
              const chamberConfig = this._getChamberConfig(route)

              // Sum ALL orders on the route for chamber marking (same on every waybill)
              const allOrders = Array.isArray(route?.orders) ? route.orders : []
              let totalRouteLiters = 0
              for (const o of allOrders) {
                const s = this._getSnapshot(o)
                const l = Number(s?.orderedFuel?.quantityLiters || 0)
                if (Number.isFinite(l)) totalRouteLiters += l
              }

              return {
                number: orderId, isLauron, sender, carrier,
                loadingPlace: "BYTÓW", date: dateStr, vehicleNo, driverName,
                receiver: { name: this._clean(receiverName) }, destination,
                attachments: "SENT / Zamówienie",
                cargo: [{ lp: 1, name: this._clean(fuelName), symbol: pm.symbol, qty: qtyLiters, unit: pm.unit, vat: pm.vat, mag: pm.mag, group: pm.group, index: pm.index, gtu: pm.gtu }],
                envTitle: "Produkt zagrażający środowisku / CN 2710", envProperLabel: "Prawidłowa nazwa przewozowa:", envLine: pm.properName || "UN 1202, Olej napędowy 3, III, (D,E)",
                adrTitle: "Informacje ADR", adrTopLine: "Próżny pojazd-cysterna, ostatni ładunek:", adrLine: pm.properName ? pm.properName.replace("(D,E)", "(D/E)") : "UN1202, Olej napędowy, 3, III, (D/E)",
                remarks: "",
                chamberConfig: chamberConfig,
                totalRouteLiters: totalRouteLiters,
              };
            },
            downloadWaybillPdf(ord) {
              try {
                if (!this.route) return;
                const cc = this._getChamberConfig(this.route)
                if (cc.mode === 'none') {
                  this.$store.commit('setNotification', {
                    show: true,
                    head: 'Brak komór w zestawie transportowym',
                    subheader: 'Uzupełnij komory pojazdu lub naczepy, aby wygenerować list przewozowy.',
                    success: false
                  })
                  return
                }
                const data = this._buildWaybillDataFromRouteAndOrder(this.route, ord);
                const dd = this._buildWaybillDd(data);
                pdfMake.createPdf(dd).download(`List_przewozowy_${data.number}.pdf`);
              } catch (e) {
                console.error("Waybill PDF error:", e);
              }
            },

            _buildChamberTable(label, chambers, marks, isEnv) {
              // Build a dynamic chamber table row
              // columns: [text_area] [gap if solo+przyczepa needed externally] [chamber1] [chamber2] ...
              if (!chambers || chambers.length === 0) return null

              const count = chambers.length
              const chamberWidths = []
              for (let i = 0; i < count; i++) chamberWidths.push(22)

              const widths = ["*", ...chamberWidths]

              // Header row
              const headerRow = [
                { text: label, style: "boxLabel", alignment: "center", colSpan: count, valign: "middle" },
              ]
              for (let i = 1; i < count; i++) headerRow.push({})

              // Number row
              const numRow = []
              for (let i = 0; i < count; i++) {
                numRow.push({ text: String(i + 1), style: "boxNum", alignment: "center", valign: "middle" })
              }

              // Mark row (X or empty)
              const markRow = []
              for (let i = 0; i < count; i++) {
                markRow.push({
                  text: isEnv ? (marks[i] || '') : '',
                  style: "boxNum",
                  alignment: "center",
                  valign: "middle",
                })
              }

              return { headerRow, numRow, markRow, count, widths: chamberWidths }
            },

            _buildEnvTable(data) {
              const cc = data.chamberConfig
              if (cc.mode === 'none') {
                // Fallback: no chambers at all
                return { table: { widths: ["*"], body: [
                  [{ text: data.envTitle, style: "boxHead", fillColor: "#f2f2f2" }],
                  [{ text: data.envProperLabel, style: "boxLabel" }],
                  [{ text: data.envLine, style: "boxText" }],
                ]}, layout: this._layoutPlain() }
              }

              const allChambers = [...cc.vehicleChambers, ...cc.trailerChambers]
              const marks = this._markChambers(allChambers, data.totalRouteLiters)
              const vMarks = marks.slice(0, cc.vehicleChambers.length)
              const tMarks = marks.slice(cc.vehicleChambers.length)

              if (cc.mode === 'solo') {
                return this._buildSingleGroupEnvTable(data, cc.vehicleChambers, vMarks, cc.vehicleLabel)
              } else if (cc.mode === 'cysterna') {
                return this._buildSingleGroupEnvTable(data, cc.trailerChambers, tMarks, cc.trailerLabel)
              } else if (cc.mode === 'solo_przyczepa') {
                return this._buildDualGroupEnvTable(data, cc, vMarks, tMarks)
              }

              return { table: { widths: ["*"], body: [[{ text: data.envTitle, style: "boxHead" }]] }, layout: this._layoutPlain() }
            },

            _buildSingleGroupEnvTable(data, chambers, marks, label) {
              const count = chambers.length
              const chamberWidths = chambers.map(() => 22)
              const widths = ["*", ...chamberWidths]

              const body = []

              // Title row
              const titleRow = [{ text: data.envTitle, style: "boxHead", colSpan: count + 1, fillColor: "#f2f2f2", alignment: "left", valign: "middle" }]
              for (let i = 0; i < count; i++) titleRow.push({})
              body.push(titleRow)

              // Label + chamber header
              const labelRow = [{ text: data.envProperLabel, style: "boxLabel", valign: "middle" }]
              if (count > 0) {
                labelRow.push({ text: label, style: "boxLabel", alignment: "center", colSpan: count, valign: "middle" })
                for (let i = 1; i < count; i++) labelRow.push({})
              }
              body.push(labelRow)

              // Text + chamber numbers
              const textRow = [{ text: data.envLine, style: "boxText", rowSpan: 2, valign: "middle" }]
              for (let i = 0; i < count; i++) {
                textRow.push({ text: String(i + 1), style: "boxNum", alignment: "center", valign: "middle" })
              }
              body.push(textRow)

              // X marks row
              const markRow = [{}]
              for (let i = 0; i < count; i++) {
                markRow.push({ text: marks[i] || '', style: "boxMark", alignment: "center", valign: "middle" })
              }
              body.push(markRow)

              return {
                table: { widths, body },
                layout: this._layoutChamberBold()
              }
            },

            _buildDualGroupEnvTable(data, cc, vMarks, tMarks) {
              const vCount = cc.vehicleChambers.length
              const tCount = cc.trailerChambers.length
              const gapWidth = 14
              const totalCols = 1 + vCount + 1 + tCount // text + vChambers + gap + tChambers
              const widths = ["*"]
              for (let i = 0; i < vCount; i++) widths.push(22)
              widths.push(gapWidth) // gap
              for (let i = 0; i < tCount; i++) widths.push(22)

              const body = []

              // Title row
              const titleRow = [{ text: data.envTitle, style: "boxHead", colSpan: totalCols, fillColor: "#f2f2f2", alignment: "left", valign: "middle" }]
              for (let i = 1; i < totalCols; i++) titleRow.push({})
              body.push(titleRow)

              // Label row: proper name label | vehicle chamber label | gap | trailer chamber label
              const labelRow = [{ text: data.envProperLabel, style: "boxLabel", valign: "middle" }]
              if (vCount > 0) {
                labelRow.push({ text: cc.vehicleLabel, style: "boxLabel", alignment: "center", colSpan: vCount, valign: "middle" })
                for (let i = 1; i < vCount; i++) labelRow.push({})
              }
              labelRow.push({ text: "", border: [false, true, false, false] }) // gap
              if (tCount > 0) {
                labelRow.push({ text: cc.trailerLabel, style: "boxLabel", alignment: "center", colSpan: tCount, valign: "middle" })
                for (let i = 1; i < tCount; i++) labelRow.push({})
              }
              body.push(labelRow)

              // Number row
              const numRow = [{ text: data.envLine, style: "boxText", rowSpan: 2, valign: "middle" }]
              for (let i = 0; i < vCount; i++) {
                numRow.push({ text: String(i + 1), style: "boxNum", alignment: "center", valign: "middle" })
              }
              numRow.push({ text: "", border: [false, true, false, false] }) // gap
              for (let i = 0; i < tCount; i++) {
                numRow.push({ text: String(i + 1), style: "boxNum", alignment: "center", valign: "middle" })
              }
              body.push(numRow)

              // Mark row
              const markRow = [{}]
              for (let i = 0; i < vCount; i++) {
                markRow.push({ text: vMarks[i] || '', style: "boxMark", alignment: "center", valign: "middle" })
              }
              markRow.push({ text: "", border: [false, false, false, false] }) // gap
              for (let i = 0; i < tCount; i++) {
                markRow.push({ text: tMarks[i] || '', style: "boxMark", alignment: "center", valign: "middle" })
              }
              body.push(markRow)

              return {
                table: { widths, body },
                layout: this._layoutChamberBoldDual(vCount)
              }
            },

            _buildAdrTable(data) {
              const cc = data.chamberConfig
              if (cc.mode === 'none') {
                return { table: { widths: ["*"], body: [
                  [{ text: data.adrTitle, style: "boxHead", fillColor: "#f2f2f2" }],
                  [{ text: data.adrTopLine, style: "boxLabel" }],
                  [{ text: data.adrLine, style: "boxText" }],
                ]}, layout: this._layoutPlain() }
              }

              if (cc.mode === 'solo') {
                return this._buildSingleGroupAdrTable(data, cc.vehicleChambers, cc.vehicleLabel)
              } else if (cc.mode === 'cysterna') {
                return this._buildSingleGroupAdrTable(data, cc.trailerChambers, cc.trailerLabel)
              } else if (cc.mode === 'solo_przyczepa') {
                return this._buildDualGroupAdrTable(data, cc)
              }

              return { table: { widths: ["*"], body: [[{ text: data.adrTitle, style: "boxHead" }]] }, layout: this._layoutPlain() }
            },

            _buildSingleGroupAdrTable(data, chambers, label) {
              const count = chambers.length
              const chamberWidths = chambers.map(() => 22)
              const widths = ["*", ...chamberWidths]
              const body = []

              const titleRow = [{ text: data.adrTitle, style: "boxHead", colSpan: count + 1, fillColor: "#f2f2f2", valign: "middle" }]
              for (let i = 0; i < count; i++) titleRow.push({})
              body.push(titleRow)

              const labelRow = [{ text: data.adrTopLine, style: "boxLabel", valign: "middle" }]
              if (count > 0) {
                labelRow.push({ text: label, style: "boxLabel", alignment: "center", colSpan: count, valign: "middle" })
                for (let i = 1; i < count; i++) labelRow.push({})
              }
              body.push(labelRow)

              const textRow = [{ text: data.adrLine, style: "boxText", rowSpan: 2, valign: "middle" }]
              for (let i = 0; i < count; i++) {
                textRow.push({ text: String(i + 1), style: "boxNum", alignment: "center", valign: "middle" })
              }
              body.push(textRow)

              // Empty mark row (ADR has no X marks)
              const markRow = [{}]
              for (let i = 0; i < count; i++) {
                markRow.push({ text: " ", style: "boxMark", alignment: "center", valign: "middle" })
              }
              body.push(markRow)

              return { table: { widths, body }, layout: this._layoutChamberBold() }
            },

            _buildDualGroupAdrTable(data, cc) {
              const vCount = cc.vehicleChambers.length
              const tCount = cc.trailerChambers.length
              const totalCols = 1 + vCount + 1 + tCount
              const widths = ["*"]
              for (let i = 0; i < vCount; i++) widths.push(22)
              widths.push(14)
              for (let i = 0; i < tCount; i++) widths.push(22)

              const body = []

              const titleRow = [{ text: data.adrTitle, style: "boxHead", colSpan: totalCols, fillColor: "#f2f2f2", valign: "middle" }]
              for (let i = 1; i < totalCols; i++) titleRow.push({})
              body.push(titleRow)

              const labelRow = [{ text: data.adrTopLine, style: "boxLabel", valign: "middle" }]
              if (vCount > 0) {
                labelRow.push({ text: cc.vehicleLabel, style: "boxLabel", alignment: "center", colSpan: vCount, valign: "middle" })
                for (let i = 1; i < vCount; i++) labelRow.push({})
              }
              labelRow.push({ text: "", border: [false, true, false, false] })
              if (tCount > 0) {
                labelRow.push({ text: cc.trailerLabel, style: "boxLabel", alignment: "center", colSpan: tCount, valign: "middle" })
                for (let i = 1; i < tCount; i++) labelRow.push({})
              }
              body.push(labelRow)

              const numRow = [{ text: data.adrLine, style: "boxText", rowSpan: 2, valign: "middle" }]
              for (let i = 0; i < vCount; i++) numRow.push({ text: String(i + 1), style: "boxNum", alignment: "center", valign: "middle" })
              numRow.push({ text: "", border: [false, true, false, false] })
              for (let i = 0; i < tCount; i++) numRow.push({ text: String(i + 1), style: "boxNum", alignment: "center", valign: "middle" })
              body.push(numRow)

              const markRow = [{}]
              for (let i = 0; i < vCount; i++) markRow.push({ text: " ", style: "boxMark", alignment: "center", valign: "middle" })
              markRow.push({ text: "", border: [false, false, false, false] })
              for (let i = 0; i < tCount; i++) markRow.push({ text: " ", style: "boxMark", alignment: "center", valign: "middle" })
              body.push(markRow)

              return { table: { widths, body }, layout: this._layoutChamberBoldDual(vCount) }
            },

            _layoutPlain() {
              return {
                hLineWidth: function(i, node) { return (i === 0 || i === 1 || i === node.table.body.length) ? 1.0 : 0.8 },
                vLineWidth: function(i, node) { return (i === 0 || i === node.table.widths.length) ? 1.0 : 0.8 },
                hLineColor: function(i, node) { return (i === 0 || i === 1 || i === node.table.body.length) ? "#111" : "#999" },
                vLineColor: function(i, node) { return (i === 0 || i === node.table.widths.length) ? "#111" : "#999" },
                paddingLeft: function() { return 4 },
                paddingRight: function() { return 4 },
                paddingTop: function() { return 3 },
                paddingBottom: function() { return 3 },
              }
            },

            _layoutChamberBold() {
              return {
                hLineWidth: function(i, node) { return (i === 0 || i === 1 || i === node.table.body.length) ? 1.0 : 0.8 },
                vLineWidth: function(i, node) {
                  if (i === 1) return 1.4
                  return (i === 0 || i === node.table.widths.length) ? 1.0 : 0.8
                },
                hLineColor: function(i, node) { return (i === 0 || i === 1 || i === node.table.body.length) ? "#111" : "#999" },
                vLineColor: function(i, node) {
                  if (i === 1) return "#111"
                  return (i === 0 || i === node.table.widths.length) ? "#111" : "#999"
                },
                paddingLeft: function() { return 4 },
                paddingRight: function() { return 4 },
                paddingTop: function() { return 3 },
                paddingBottom: function() { return 3 },
              }
            },

            _layoutChamberBoldDual(vCount) {
              // Bold lines around the text column, the gap, and outer borders
              const gapCol = 1 + vCount // index of the gap column
              return {
                hLineWidth: function(i, node) { return (i === 0 || i === 1 || i === node.table.body.length) ? 1.0 : 0.8 },
                vLineWidth: function(i, node) {
                  if (i === 1 || i === gapCol || i === gapCol + 1) return 1.4
                  return (i === 0 || i === node.table.widths.length) ? 1.0 : 0.8
                },
                hLineColor: function(i, node) { return (i === 0 || i === 1 || i === node.table.body.length) ? "#111" : "#999" },
                vLineColor: function(i, node) {
                  if (i === 1 || i === gapCol || i === gapCol + 1) return "#111"
                  return (i === 0 || i === node.table.widths.length) ? "#111" : "#999"
                },
                paddingLeft: function() { return 4 },
                paddingRight: function() { return 4 },
                paddingTop: function() { return 3 },
                paddingBottom: function() { return 3 },
              }
            },

            _buildWaybillDd(data) {
              function headerCell(txt) { return { text: txt, style: "th", alignment: "center", valign: "middle", margin: [0, 3, 0, 3] }; }
              function bodyCell(txt, align) { return { text: (txt === null || typeof txt === "undefined") ? "" : String(txt), style: "td", alignment: align || "left", valign: "middle", margin: [4, 3, 4, 3] }; }
              function receiverNameOnly(a) { if (!a) return ""; return a.name || ""; }
              function fmtSender(a) {
                if (!a) return "";
                var lines = [];
                if (a.name) lines.push(a.name);
                if (a.nip) lines.push("NIP: " + a.nip);
                else if (a.bdo) lines.push(a.bdo);
                if (a.street) lines.push(a.street);
                var pcCity = ((a.postalCode || "") + " " + (a.city || "")).replace(/\s+/g, " ").trim();
                if (pcCity) lines.push(pcCity);
                return lines.join("\n");
              }
              function layoutCargoOuterBoldHeaderFill() {
                return { hLineWidth: function(i,node){return(i===0||i===1||i===node.table.body.length)?1.2:0.8;}, vLineWidth: function(i,node){return(i===0||i===node.table.widths.length)?1.2:0.8;}, hLineColor: function(i,node){return(i===0||i===1||i===node.table.body.length)?"#111":"#999";}, vLineColor: function(i,node){return(i===0||i===node.table.widths.length)?"#111":"#999";}, paddingLeft:function(){return 4;}, paddingRight:function(){return 4;}, paddingTop:function(){return 2;}, paddingBottom:function(){return 2;}, fillColor:function(rowIndex){return rowIndex===0?"#f2f2f2":null;} };
              }

              var cargoBody = [];
              cargoBody.push([headerCell("Lp."),headerCell("Nazwa towaru/usługi (PKWiU)"),headerCell("Symbol"),headerCell("Ilość"),headerCell("Jedn. miary"),headerCell("VAT (%)"),headerCell("Mag."),headerCell("Grupa t."),headerCell("Indeks (PLU)"),headerCell("GTU")]);
              for (var i = 0; i < (data.cargo || []).length; i++) {
                var r = data.cargo[i];
                cargoBody.push([bodyCell(r.lp||(i+1),"center"),bodyCell(r.name,"left"),bodyCell(r.symbol,"center"),bodyCell(r.qty,"center"),bodyCell(r.unit,"center"),bodyCell(r.vat,"center"),bodyCell(r.mag,"center"),bodyCell(r.group,"center"),bodyCell(r.index,"center"),bodyCell(r.gtu,"center")]);
              }

              // Build dynamic chamber tables
              var envTable = this._buildEnvTable(data);
              var adrTable = this._buildAdrTable(data);

              return {
                pageSize:"A4", pageMargins:[28,24,28,24],
                content:[
                  {columns:[{text:"LIST PRZEWOZOWY",style:"title",width:"*"},{text:"Nr: "+(data.number||""),style:"titleNo",alignment:"right",width:220}],margin:[0,0,0,10]},
                  {columns:[{width:"*",stack:[{text:"Nadawca:",style:"label"},{text:fmtSender(data.sender),style:"block"}]},{width:"*",stack:[{text:"Przewoźnik:",style:"label"},{text:fmtSender(data.carrier),style:"block"}]},{width:"*",stack:[{text:"Miejsce załadunku:",style:"label"},{text:data.loadingPlace||"",style:"blockNoBold"},{text:"Data załadunku:",style:"label",margin:[0,6,0,0]},{text:data.date||"",style:"blockNoBold"}]}],columnGap:16,margin:[0,0,0,10]},
                  {columns:[{width:"*",stack:[{text:"Nr samochodu",style:"labelSmall"},{text:data.vehicleNo||"",style:"valueLine"}]},{width:"*",stack:[{text:"Imię i Nazwisko kierowcy",style:"labelSmall"},{text:data.driverName||"",style:"valueLine"}]},{width:"*",text:""}],columnGap:16,margin:[0,0,0,12]},
                  {columns:[{width:"*",stack:[{text:"Odbiorca:",style:"label"},{text:receiverNameOnly(data.receiver),style:"block"}]},{width:"*",stack:[{text:"Miejsce przeznaczenia:",style:"label"},{text:data.destination||"",style:"block"}]},{width:"*",stack:[{text:"Załączone dokumenty:",style:"label"},{text:String(data.attachments||""),style:"block"},{text:"(niepotrzebne skreślić)",style:"hint",margin:[0,6,0,0]}]}],columnGap:16,margin:[0,0,0,10]},
                  {text:"Informacje o ładunku:",style:"sectionTitle",margin:[0,8,0,8]},
                  {table:{headerRows:1,widths:[22,"*",30,50,40,34,28,30,40,30],body:cargoBody},layout:layoutCargoOuterBoldHeaderFill(),margin:[0,0,0,16]},
                  {stack:[envTable],margin:[0,0,0,10]},
                  {stack:[adrTable],margin:[0,0,0,18]},
                  {text:"UWAGI:",style:"label"},{text:data.remarks||"",style:"block",margin:[0,2,0,12]},
                  {text:"Potwierdzenie dostawy:",style:"label",margin:[0,8,0,10]},
                  {columns:[{width:"*",text:""},{width:260,stack:[{canvas:[{type:"line",x1:0,y1:0,x2:260,y2:0,lineWidth:1}]},{text:"(podpis odbiorcy)",style:"hint",alignment:"center",margin:[0,6,0,0]}]}]},
                ],
                styles:{title:{fontSize:16,bold:true,color:"#111"},titleNo:{fontSize:12,bold:true,color:"#111"},sectionTitle:{fontSize:11,bold:true,color:"#111"},label:{fontSize:9,bold:true,color:"#111"},labelSmall:{fontSize:8,bold:true,color:"#111"},hint:{fontSize:7,color:"#444"},block:{fontSize:9,color:"#111",lineHeight:1.15,margin:[0,2,0,0]},blockNoBold:{fontSize:9,bold:false,color:"#111",lineHeight:1.15,margin:[0,2,0,0]},valueLine:{fontSize:10,bold:true,color:"#111",margin:[0,2,0,0]},th:{fontSize:6.8,bold:true,color:"#111"},td:{fontSize:6.8,color:"#111"},boxHead:{fontSize:6.9,bold:true,color:"#111"},boxLabel:{fontSize:6.6,bold:true,color:"#111"},boxText:{fontSize:6.6,color:"#111"},boxNum:{fontSize:6.6,color:"#111"},boxMark:{fontSize:8,bold:true,color:"#111",margin:[0,2,0,2]}},
                defaultStyle:{fontSize:9,color:"#111"},
              };
            },
            unixToMomentLL(unixTime) {
                moment.locale('pl');
                return moment.unix(unixTime).format("LL");
            },
            removeRefsForDisplay() {
                let routeCopy = JSON.parse(JSON.stringify(this.route))
                if (routeCopy.driver != null) routeCopy.driver.ref = null
                for (const key in routeCopy.orders) {
                    if (routeCopy.orders[key].ref) routeCopy.orders[key].ref = null
                }
                if (routeCopy.vehicle != null) routeCopy.vehicle.ref = null
                if (routeCopy.trailer != null) routeCopy.trailer.ref = null
                return routeCopy
            },
          removeOrderFromEdit(index) {
            const removed = this.editData.orders[index]
            if (removed) this.editData.removedOrders.push(removed)
            this.editData.orders.splice(index, 1)
          },

          async cancelRoute() {
            if (!this.route || !this.route.id) return
            this.cancelingRoute = true
            try {
              const batch = db.batch()
              if (Array.isArray(this.route.orders)) {
                this.route.orders.forEach(ord => {
                  if (ord.freshSnapshot === null) return
                  const ordId = ord.id || (ord.ref && ord.ref.id) || null
                  if (ordId) batch.update(db.collection('FuelOrders').doc(ordId), { isInRoute: false, routeRef: null })
                })
              }
              batch.update(db.collection('Routes').doc(this.route.id), { status: 4, archive: true })
              await batch.commit()
              this.route.status = 4
              this.route.archive = true
              this.showCancelModal = false
              this.$store.commit('setNotification', { show: true, head: 'Trasa anulowana', subheader: 'Punkty trasy zostały odpięte.', success: true })
            } catch (e) {
              console.error(e)
              this.$store.commit('setNotification', { show: true, head: 'Błąd', subheader: 'Nie udało się anulować trasy.', success: false })
            } finally {
              this.cancelingRoute = false
            }
          },

          async openAddOrdersModal() {
            this.addOrdersModal.show = true
            this.addOrdersModal.checkedIds = []
            this.addOrdersModal.selectedById = {}
            this.addOrdersModal.filterRegion = 'all'
            this.addOrdersModal.removedOrdersList = [...this.editData.removedOrders]
            if (this.regions.all.length === 0) await this.getRegions()
            await this.fetchAddOrders()
          },

          closeAddOrdersModal() {
            if (this.addOrdersModal.listener) {
              this.addOrdersModal.listener()
              this.addOrdersModal.listener = null
            }
            this.addOrdersModal.show = false
          },

          async fetchAddOrders() {
            if (this.addOrdersModal.listener) {
              this.addOrdersModal.listener()
              this.addOrdersModal.listener = null
            }
            this.addOrdersModal.loading = true
            let q = db.collection('FuelOrders')
              .where('status', '==', 2)
            if (this.addOrdersModal.filterRegion !== 'all') {
              q = db.collection('FuelOrders')
                .where('status', '==', 2)
                .where('deliveryAddress.regionName', '==', this.addOrdersModal.filterRegion)
            }
            q = q.orderBy('deliveryDate.to.unix', 'asc')
            // Lista ID niedostarczonych zamówień w aktualnej trasie — pokazujemy je w puli,
            // ale oznaczamy jako "Reaktywacja" żeby było jasne, że ponowne dodanie odblokuje istniejący wpis
            const undeliveredInRoute = new Set(
              (this.editData.orders || [])
                .filter(o => o.undelivered === true)
                .map(o => o.id || (o.ref && o.ref.id))
                .filter(Boolean)
            )
            // IDs zamówień aktualnie aktywnych/dostarczonych w trasie (nie w stanie "niedostarczone") — wykluczamy
            const activeRouteIds = new Set(
              (this.editData.orders || [])
                .filter(o => o.undelivered !== true)
                .map(o => o.id || (o.ref && o.ref.id))
                .filter(Boolean)
            )
            this.addOrdersModal.listener = q.limit(40).onSnapshot(snapshot => {
              this.addOrdersModal.orders = []
              snapshot.forEach(doc => {
                if (activeRouteIds.has(doc.id)) return
                const d = doc.data()
                d.id = doc.id
                d._isReactivation = undeliveredInRoute.has(doc.id)
                this.addOrdersModal.orders.push(d)
              })
              this.addOrdersModal.limitReached = snapshot.docs.length < 40
              this.addOrdersModal.loading = false
            }, err => {
              console.error(err)
              this.addOrdersModal.loading = false
            })
          },

          toggleAddOrder(order) {
            const id = order.id
            if (this.addOrdersModal.checkedIds.includes(id)) {
              this.addOrdersModal.checkedIds = this.addOrdersModal.checkedIds.filter(x => x !== id)
              delete this.addOrdersModal.selectedById[id]
            } else {
              this.addOrdersModal.checkedIds.push(id)
              // If it's a previously removed order (has snapshotAtAddingTime already), use it directly
              const isRemoved = this.addOrdersModal.removedOrdersList.some(o => o.id === id)
              if (isRemoved) {
                this.addOrdersModal.selectedById[id] = order
              } else {
                this.addOrdersModal.selectedById[id] = {
                  arrivalUnix: null, id,
                  ref: db.collection('FuelOrders').doc(id),
                  snapshotAtAddingTime: {
                    addingTimeUnix: moment().unix(),
                    companyName: order.client?.invoiceAddress?.name || '—',
                    deliveryAddress: { city: order.deliveryAddress?.city || '—', postCode: order.deliveryAddress?.postCode || '—', regionName: order.deliveryAddress?.regionName || 'Nie przypisano', address: order.deliveryAddress?.address || '—' },
                    orderedFuel: { fuelId: order.orderedFuel?.fuelId || '—', fuelName: order.orderedFuel?.fuelName || '—', quantityLiters: order.orderedFuel?.quantityLiters || '—' },
                  },
                  status: 'planned'
                }
              }
            }
          },

          async confirmAddOrders() {
            if (this.addOrdersModal.checkedIds.length === 0) return
            let idx = this.editData.orders.length
            for (const id of this.addOrdersModal.checkedIds) {
              // 1. Czy już istnieje w editData.orders jako niedostarczone → reaktywuj
              const existingIdx = this.editData.orders.findIndex(o => (o.id || (o.ref && o.ref.id)) === id)
              if (existingIdx !== -1) {
                const existing = this.editData.orders[existingIdx]
                const reactivated = { ...existing, undelivered: false, _reactivated: true }
                // freshSnapshot zostaje stary — dopiero po zapisie FuelOrder dostanie status 4,
                // canFinishRoute zadziała bo `undelivered === false` i freshSnapshot.status !== 5
                this.editData.orders.splice(existingIdx, 1, reactivated)
                continue
              }
              // 2. Przywrócenie z listy usuniętych (w trybie edycji)
              const removedIdx = this.editData.removedOrders.findIndex(o => o.id === id)
              if (removedIdx !== -1) {
                const restored = this.editData.removedOrders.splice(removedIdx, 1)[0]
                restored.sortIndex = idx++
                this.editData.orders.push(restored)
                continue
              }
              // 3. Nowe zamówienie z puli
              const order = { ...this.addOrdersModal.selectedById[id], sortIndex: idx++ }
              this.editData.orders.push(order)
            }
            this.closeAddOrdersModal()
          },

          async getDrivers() {
            try {
              this.drivers.all = []
              const snap = await db.collection('WorkersData').where('permissions.driver', '==', true).orderBy('name', 'asc').get()
              snap.forEach(doc => { this.drivers.all.push({ id: doc.id, ...doc.data() }) })
            } catch (e) { console.error(e) }
          },

          async getRegions() {
            try {
              this.regions.all = []
              const doc = await db.collection('routeRegions').doc('regions').get()
              if (doc.exists) this.regions.all = doc.data().regions || []
            } catch (e) { console.error(e) }
          },

          async getVehicles() {
            try {
              const snap = await db.collection('Fleet').where('vehicle', '==', true).orderBy('meta.created.unix', 'desc').get()
              const list = []
              snap.forEach(doc => { const d = doc.data(); d.id = doc.id; d.available = d.active !== false; list.push(d) })
              list.sort((a, b) => (a.available && !b.available ? -1 : !a.available && b.available ? 1 : 0))
              this.vehicles = list
            } catch (e) { console.error(e) }
          },

          async getTrails() {
            try {
              const snap = await db.collection('Fleet').where('vehicle', '==', false).orderBy('meta.created.unix', 'desc').get()
              const list = []
              snap.forEach(doc => { const d = doc.data(); d.id = doc.id; d.available = d.active !== false; list.push(d) })
              list.sort((a, b) => (a.available && !b.available ? -1 : !a.available && b.available ? 1 : 0))
              this.trails = list
            } catch (e) { console.error(e) }
          },

          todayAt00() {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            return today
          },

          periodDateFormat() {
            if (!this.editDate) return ''
            return `${moment(this.editDate[0]).format('DD.MM.YYYY HH:mm')} - ${moment(this.editDate[1]).format('DD.MM.YYYY HH:mm')}`
          },

            async downloadRouteDetails() {
                const routeId = this.$route.query.id
                try {
                    const routeDoc = await db.collection('Routes').doc(routeId).get()
                    if (routeDoc.exists) {
                        this.route = routeDoc.data()
                        this.route.id = routeDoc.id
                        this.downloadOrdersData()
                    } else {
                        console.error('No such route!')
                    }
                } catch (error) {
                    console.error('Error fetching route details:', error)
                }
            },
            async downloadOrdersData() {
                if (!this.route || !this.route.orders) return
                const updated = await Promise.all(
                    this.route.orders.map(async (ord) => {
                        const entry = { ...ord, freshSnapshot: null }
                        const ordId = ord.id || (ord.ref && ord.ref.id) || null
                        if (ordId) {
                            try {
                                const doc = await db.collection('FuelOrders').doc(ordId).get()
                                if (doc.exists) entry.freshSnapshot = doc.data()
                                else console.error(`No such order ${ordId} for route ${this.route.id}!`)
                            } catch (err) {
                                console.error(`Error fetching order for route ${this.route.id}:`, err)
                            }
                        }
                        return entry
                    })
                )
                this.route.orders = updated
                // Po pobraniu fresh snapshots — odśwież statusy SENT z urzad-api
                this.refreshAllSentStatuses().catch(() => {})
            }
        },
        computed: {
          canFinishRoute() {
            // Trasa musi być w realizacji (status 2) — z zaplanowanej nie można od razu zakończyć
            if (this.route?.status !== 2) return false
            // W trybie edycji liczymy aktualną listę z editData (uwzględnia świeżo dodane punkty)
            const orders = this.editMode ? (this.editData.orders || []) : (this.route?.orders || [])
            if (orders.length === 0) return false
            // Każde zamówienie musi być rozliczone: dostarczone (status 5) LUB oznaczone niedostarczone
            return orders.every(o => o.undelivered === true || (o.freshSnapshot && o.freshSnapshot.status === 5))
          },
          hasDeliveredOrders() {
            const orders = this.editMode ? (this.editData.orders || []) : (this.route?.orders || [])
            return orders.some(o => o.freshSnapshot && o.freshSnapshot.status === 5)
          },
          finishBlockReason() {
            if (this.route?.status !== 2) return 'Trasę można zakończyć dopiero po jej rozpoczęciu.'
            const orders = this.editMode ? (this.editData.orders || []) : (this.route?.orders || [])
            if (orders.length === 0) return 'Trasa nie zawiera żadnych zamówień.'
            return 'Oznacz wszystkie zamówienia jako dostarczone lub niedostarczone aby zakończyć trasę.'
          },
          hasInvoiceableOrders() {
            return (this.route?.orders || []).some(o => {
              if (o.undelivered) return false
              const snap = o.freshSnapshot
              return snap && snap.status === 5 && snap.unloadedFuel && !snap.invoice?.invoiced
            })
          },
          isArchived() {
            return this.route?.archive === true || this.route?.status === 3 || this.route?.status === 4
          },
          fullSelectedVehicle() {
            if (!this.editSelectedVehicles) return ''
            return `${this.editSelectedVehicles.brand} ${this.editSelectedVehicles.licensePlate}`
          },
          fullSelectedTrail() {
            if (!this.editSelectedTrails) return ''
            return `${this.editSelectedTrails.brand} ${this.editSelectedTrails.licensePlate}`
          },
          displayOrders() {
            if (this.editMode) return this.editData.orders
            return this.route?.orders || []
          },
          totalLiters() {
            const orders = this.route?.orders || [];
            let sum = 0;
            for (const ord of orders) {
              const snap = this._getSnapshot(ord);
              const liters = Number(snap?.orderedFuel?.quantityLiters || 0);
              if (Number.isFinite(liters)) sum += liters;
            }
            return sum;
          },
          totalCapacity() {
            const t = Number(this.route?.totalLitreCapacity);
            if (Number.isFinite(t) && t > 0) return t;
            const v = Number(this.route?.vehicle?.tankLitreCapacity || 0);
            const tr = Number(this.route?.trailer?.tankLitreCapacity || 0);
            return (Number.isFinite(v) ? v : 0) + (Number.isFinite(tr) ? tr : 0);
          },
          capacityPercent() {
            const cap = this.totalCapacity || 0;
            if (!cap) return 0;
            const p = (this.totalLiters / cap) * 100;
            return Math.max(0, Math.min(100, Math.round(p)));
          },
          capacityLabel() {
            const cap = this.totalCapacity || 0;
            return `${this.totalLiters} / ${cap} L`;
          },
        }
    }
</script>

<style scoped>
.pageShell {
  border-radius: 26px;
}

.sectionCard {
  overflow: visible;
  position: relative;
  min-width: 0;
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.sectionHead {
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba(71, 85, 105, 0.65);
}

.sectionTitle {
  font-size: 15px;
  line-height: 1.3;
  font-weight: 700;
  color: #f8fafc;
}

.sectionSub {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: #94a3b8;
}

.sectionBody {
  padding: 16px 18px 18px;
}

/* Soft info boxes */
.softBox {
  border-radius: 18px;
  border: 1px solid rgba(100, 116, 139, 0.45);
  background: rgba(15, 23, 42, 0.22);
  padding: 12px 14px;
  min-width: 0;
}

.softBoxStrong {
  border-color: rgba(59, 130, 246, 0.35);
  background: rgba(30, 41, 59, 0.45);
}

.softBoxLabel {
  font-size: 11px;
  font-weight: 600;
  color: rgba(148, 163, 184, 1);
}

.softBoxValue {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(226, 232, 240, 1);
  line-height: 1.6;
  overflow-wrap: break-word;
  word-break: break-word;
  min-width: 0;
}

.softBoxValueStrong {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

/* Status chips */
.statusChip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid;
}

.statusChipBlue {
  border-color: rgba(59, 130, 246, 0.35);
  background: rgba(37, 99, 235, 0.12);
  color: #93c5fd;
}

.statusChipYellow {
  border-color: rgba(234, 179, 8, 0.35);
  background: rgba(234, 179, 8, 0.12);
  color: #fde68a;
}

.statusChipGreen {
  border-color: rgba(34, 197, 94, 0.35);
  background: rgba(34, 197, 94, 0.12);
  color: #86efac;
}

.statusChipRed {
  border-color: rgba(239, 68, 68, 0.35);
  background: rgba(239, 68, 68, 0.12);
  color: #fca5a5;
}

/* Section badge chips */
.chipBadge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.65);
  background: rgba(30, 41, 59, 0.8);
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #e2e8f0;
  white-space: nowrap;
}

/* Chamber chips */
.chamberChip {
  display: inline-flex;
  align-items: center;
  border-radius: 10px;
  border: 1px solid rgba(100, 116, 139, 0.5);
  background: rgba(51, 65, 85, 0.4);
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #cbd5e1;
}

/* Fields for edit mode */
.fieldWrap {
  display: flex;
  flex-direction: column;
}

.fieldLabel {
  display: block;
  margin-bottom: 7px;
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
}

.fieldInput {
  display: block;
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.fieldInput:focus {
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.fieldTextarea {
  min-height: 100px;
  resize: vertical;
}

/* Order move buttons */
.orderMoveBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border-radius: 6px;
  border: 1px solid rgba(100, 116, 139, 0.6);
  background: rgba(51, 65, 85, 0.5);
  color: #cbd5e1;
  transition: all 0.15s ease;
}

.orderMoveBtn:hover:not(:disabled) {
  background: rgba(71, 85, 105, 0.9);
  color: #ffffff;
}

/* Drag over highlight */
.dragOver {
  background: rgba(99, 102, 241, 0.08) !important;
  box-shadow: inset 0 -2px 0 0 rgba(99, 102, 241, 0.5);
}

/* Buttons */
.btnSecondary,
.btnPrimary {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  transition: 180ms ease;
  white-space: nowrap;
}

.btnSecondary {
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  color: #e2e8f0;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnPrimary {
  border: 1px solid rgba(59, 130, 246, 0.5);
  background: rgba(37, 99, 235, 0.2);
  color: #dbeafe;
}

.btnPrimary:hover {
  background: rgba(37, 99, 235, 0.3);
}

.btnPrimary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btnDanger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 12px;
  border: 1px solid rgba(239, 68, 68, 0.4);
  background: rgba(239, 68, 68, 0.15);
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #fca5a5;
  cursor: pointer;
  transition: 180ms ease;
}
.btnDanger:hover {
  background: rgba(239, 68, 68, 0.28);
  color: #fee2e2;
}

/* File action buttons in table */
.fileBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  border: 1px solid;
  transition: 180ms ease;
  cursor: pointer;
}

.fileBtnTeal {
  border-color: rgba(20, 184, 166, 0.35);
  background: rgba(20, 184, 166, 0.08);
  color: #5eead4;
}

.fileBtnTeal:hover {
  background: rgba(20, 184, 166, 0.18);
}

.fileBtnBlue {
  border-color: rgba(59, 130, 246, 0.35);
  background: rgba(37, 99, 235, 0.08);
  color: #93c5fd;
}

.fileBtnBlue:hover {
  background: rgba(37, 99, 235, 0.18);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Group label inside route info card */
.infoGroupLabel {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(100, 116, 139, 0.9);
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(71, 85, 105, 0.45);
  margin-bottom: 4px;
}

/* Prevent grid children from overflowing */
.grid > * {
  min-width: 0;
}

/* Listbox */
.listboxButton {
  box-sizing: border-box;
  display: block;
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 42px 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  position: relative;
  text-align: left;
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}
.listboxButton:focus {
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}
.listboxOptions {
  position: absolute;
  z-index: 60;
  margin-top: 8px;
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  border-radius: 16px;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(15, 23, 42, 0.98);
  padding: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
}
.statusDot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  flex-shrink: 0;
}

/* Inline filter */
.filterFieldInline {
  display: block;
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(100, 116, 139, 0.65);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 7px 32px 7px 12px;
  font-size: 13px;
  line-height: 1.3;
  outline: none;
  appearance: none;
  transition: border-color 180ms ease;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 16px 16px;
}
.filterFieldInline:focus {
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

/* Ghost xs button */
.btnGhostXs {
  min-height: 36px;
  padding: 0 10px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  border: 1px dashed rgba(100, 116, 139, 0.65);
  background: transparent;
  color: #cbd5e1;
  transition: 180ms ease;
  white-space: nowrap;
}
.btnGhostXs:hover {
  background: rgba(51, 65, 85, 0.4);
}

/* Datepicker fake (when modal open) */
.datePickerFake {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.5);
  padding: 11px 14px;
  width: 100%;
  box-sizing: border-box;
  pointer-events: none;
}
.datePickerFakeText {
  font-size: 14px;
  color: rgba(148, 163, 184, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.backdrop-soft {
  backdrop-filter: blur(3px);
}

/* Datepicker */
.datePickerWrap {
  position: relative;
  z-index: 80;
  width: 100%;
  max-width: 100%;
  overflow: visible;
}
:deep(.dp__main) { width: 100%; max-width: 100%; }
:deep(.dp__input_wrap) { width: 100%; max-width: 100%; }
:deep(.dp__input) {
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 36px 11px 38px;
  font-size: 14px;
  line-height: 1.3;
}
:deep(.dp__input::placeholder) { color: rgba(148, 163, 184, 0.75); }
:deep(.dp__input_focus) { border-color: rgba(99, 102, 241, 0.85) !important; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important; }
:deep(.dp__icon), :deep(.dp__clear_icon) { color: rgba(148, 163, 184, 0.9); }
:deep(.dp__menu) { z-index: 120 !important; margin-top: 8px; border-radius: 16px; border: 1px solid rgba(71, 85, 105, 0.85); background: rgba(15, 23, 42, 0.98) !important; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35); overflow: hidden; }
:deep(.dp__menu_index) { z-index: 120 !important; }
:deep(.dp__menu_inner), :deep(.dp__instance_calendar), :deep(.dp__calendar), :deep(.dp__calendar_wrap), :deep(.dp__overlay), :deep(.dp__action_row), :deep(.dp__month_year_row), :deep(.dp__calendar_header), :deep(.dp__time_picker) { background: rgba(15, 23, 42, 0.98) !important; color: #f8fafc !important; }
:deep(.dp__arrow_top) { display: none !important; }
:deep(.dp__month_year_select) { color: #f8fafc !important; }
:deep(.dp__btn), :deep(.dp__icon_btn), :deep(.dp__inner_nav), :deep(.dp__inc_dec_button), :deep(.dp__pm_am_button) { color: #cbd5e1 !important; background: transparent !important; border: none !important; }
:deep(.dp__btn:hover), :deep(.dp__icon_btn:hover), :deep(.dp__inner_nav:hover), :deep(.dp__inc_dec_button:hover) { background: rgba(51, 65, 85, 0.9) !important; color: #ffffff !important; border: none !important; }
:deep(.dp__week_day) { color: rgba(148, 163, 184, 1) !important; font-weight: 600; }
:deep(.dp__calendar_item) { color: #e2e8f0 !important; }
:deep(.dp__cell_inner) { border-radius: 10px !important; color: inherit !important; box-shadow: none !important; border: 0 !important; }
:deep(.dp__cell_inner:hover) { background: rgba(51, 65, 85, 0.9) !important; color: #ffffff !important; }
:deep(.dp__range_start), :deep(.dp__range_end), :deep(.dp__active_date) { background: rgba(37, 99, 235, 0.95) !important; color: #ffffff !important; border-radius: 10px !important; border: 0 !important; }
:deep(.dp__range_between) { background: rgba(37, 99, 235, 0.18) !important; color: #dbeafe !important; border: 0 !important; }

/* Spinner */
.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(99, 102, 241, 0.25);
  border-top-color: rgba(99, 102, 241, 0.9);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>